<template>
  <card-dialog
    v-model="modalVisible"
    max-width="600px"
    :title="eventForm.id ? $t('event_details.edit_event.dialog.header') : $t('new_event.dialog.header')"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <v-btn-toggle
          v-model="eventTypeSelector"
          color="primary"
          mandatory
          class="v-btn-toggle--block mb-5"
        >
          <v-btn
            :value="eventTypeTraining"
            :disabled="!editingRules.canEditType || !editingRules.canEditToTraining"
            @click="eventForm.event_type = eventTypeTraining"
          >
            {{ $t('event_type.training') }}
          </v-btn>
          <v-btn
            :value="eventTypeMatch"
            :disabled="!editingRules.canEditType || !editingRules.canEditToMatch"
            @click="eventForm.event_type = eventTypeMatch; eventForm.is_official_match = true"
          >
            {{ $t('event_type.match') }}
          </v-btn>
          <v-btn
            :value="$t('event_type.other')"
            :disabled="!editingRules.canEditType || !editingRules.canEditToOther"
            @click="eventForm.event_type = null"
          >
            {{ $t('event_type.other') }}
          </v-btn>
        </v-btn-toggle>

        <validation-provider
          v-if="eventTypeSelector === $t('event_type.other')"
          v-slot="{ errors }"
          key="eventFormInputEventType"
          rules="required"
        >
          <v-select
            v-model="eventForm.event_type"
            outlined
            :label="$t('dropdown.event_type.title')"
            :items="otherEventTypes"
            item-value="key"
            item-text="text"
            item-disabled="disabled"
            :disabled="!editingRules.canEditType"
            clearable
            :error-messages="errors"
          />
        </validation-provider>

        <v-menu
          offset-y
          :close-on-content-click="false"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              outlined
              block
              class="mb-4"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon
                left
                :color="eventColorPreview"
              >
                fas fa-circle
              </v-icon>
              {{ $t('event_form.color.label') }}
              <v-spacer />
              <v-icon
                v-if="eventForm.color_override"
                small
                @click.stop="eventForm.color_override = null"
              >
                fal fa-times
              </v-icon>
            </v-btn>
          </template>
          <v-sheet class="pa-4">
            <color-swatch-picker
              :value="eventForm.color_override"
              allow-clear
              @input="v => eventForm.color_override = v"
            />
          </v-sheet>
        </v-menu>

        <v-select
          v-if="isFederation"
          v-model="eventForm.participant_level"
          :items="participantLevelOptions"
          :label="$t('event_group.participant_level.title')"
          :placeholder="$t('event_group.participant_level.placeholder')"
          outlined
          dense
          clearable
        />

        <template v-if="isFieldHockey && eventForm.event_type === eventTypeTraining">
          <v-select
            v-model="eventForm.training_type"
            :items="fieldHockeyTrainingTypeOptions"
            :label="$t('training_protocol.type.title')"
            :placeholder="$t('training_protocol.type.placeholder')"
            outlined
            dense
            clearable
            @change="eventForm.training_method = null"
          />
          <v-select
            v-if="fieldHockeyTrainingMethodOptions.length > 0"
            v-model="eventForm.training_method"
            :items="fieldHockeyTrainingMethodOptions"
            :label="$t('training_protocol.method.title')"
            :placeholder="$t('training_protocol.method.placeholder')"
            outlined
            dense
            clearable
          />
        </template>

        <validation-provider
          v-slot="{ errors }"
          key="eventFormInputTitle"
          rules="required"
        >
          <v-text-field
            v-model="eventForm.title"
            :error-messages="errors"
            :label="$t('text_input.title.title')"
            :placeholder="$t('text_input.title.placeholder')"
            outlined
          />
        </validation-provider>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-clock
          </v-icon> {{ $t('picker.time.title') }}
        </subtitle>

        <v-row no-gutters>
          <v-col cols="4">
            <validation-provider
              v-slot="{ errors }"
              key="eventFormInputDate"
              rules="required"
            >
              <v-menu
                v-model="dateActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="eventForm.date ? $dayjs(eventForm.date).format(getDateTimeFormatForUser('DD/MM/YYYY')) : ''"
                    :error-messages="errors"
                    :label="$t('picker.date.title')"
                    outlined
                    dense
                    :disabled="!editingRules.canEditTime"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="eventForm.date"
                  :first-day-of-week="1"
                  no-title
                  @input="processRepeatItemChange(); dateActive = false"
                />
              </v-menu>
            </validation-provider>
          </v-col>
          <v-col class="pl-2">
            <validation-provider
              v-slot="{ errors }"
              key="eventFormInputStartTime"
              rules="required|time"
            >
              <v-combobox
                v-model="eventForm.start_at"
                :error-messages="errors"
                dense
                outlined
                :disabled="!editingRules.canEditTime || eventForm.all_day"
                :items="timeItems"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="auto"
            class="pl-2 pt-3"
          >
            -
          </v-col>
          <v-col class="pl-2">
            <validation-provider
              v-slot="{ errors }"
              key="eventFormInputEndTime"
              rules="required|time"
            >
              <v-combobox
                v-model="eventForm.end_at"
                :error-messages="errors"
                dense
                outlined
                :disabled="!editingRules.canEditTime || eventForm.all_day"
                :items="timeItems"
              />
            </validation-provider>
          </v-col>
          <v-col
            v-if="eventForm.event_type === eventTypeIndividualWork || eventForm.event_type === eventTypeMatch || eventForm.event_type === eventTypeMassage || eventForm.event_type === eventTypeRest"
            cols="auto"
            class="pl-4 pt-2"
          >
            <v-switch
              v-model="eventForm.all_day"
              class="pa-0 ma-0"
              hide-details
              :disabled="!editingRules.canEditTime"
              :label="$t('all_day.label')"
              @change="onAllDayToggle"
            />
          </v-col>
        </v-row>

        <v-alert
          v-if="endBeforeStart"
          class="caption pa-2"
          type="warning"
          outlined
          color="orange darken-2"
          icon="fal fa-exclamation-triangle"
        >
          <i18n path="event_details.timing_alert">
            <template v-slot:endAt>
              <strong>{{ endAt.add(1, 'day').format(getDateTimeFormatForUser('ddd, MMMM D, HH:mm')) }}</strong>
            </template>
          </i18n>
        </v-alert>

        <validation-provider
          v-if="hasTimezoneManagement"
          v-slot="{ errors }"
          key="eventFormInputTz"
          rules="required"
        >
          <v-autocomplete
            v-model="eventForm.tz"
            outlined
            :items="timezones"
            item-value="tzCode"
            item-text="name"
            dense
            :placeholder="$t('dropdown.current_timezone.placeholder')"
            :disabled="!editingRules.canEditTime"
            :error-messages="errors"
          />
        </validation-provider>

        <v-row
          no-gutters
          align="center"
          :class="{ 'mb-6': !eventForm.repeats, 'mb-3': eventForm.repeats }"
        >
          <v-col cols="auto">
            <subtitle>
              <v-icon
                left
                small
              >
                fal fa-refresh
              </v-icon> {{ $t('switch.repeat') }}
            </subtitle>
          </v-col>
          <v-col
            cols="auto"
            class="pl-4"
          >
            <v-switch
              v-model="eventForm.repeats"
              class="pa-0 ma-0"
              hide-details
              :disabled="!editingRules.canEditTime"
              @change="processRepeatItemChange()"
            />
          </v-col>
          <template v-if="eventForm.id && eventForm.event_schedule">
            <v-spacer />
            <v-col
              class="grey--text text-caption"
              cols="auto"
            >
              {{ $t("repeat_settings.label") }}
            </v-col>
          </template>
        </v-row>

        <div v-if="eventForm.repeats">
          <validation-provider
            v-slot="{ errors }"
            key="eventFormInputRepeatMode"
            rules="required"
          >
            <v-select
              v-model="repeatMode"
              dense
              outlined
              :items="repeatItems"
              :label="$t('dropdown.repeat.title')"
              :error-messages="errors"
              :disabled="!editingRules.canEditTime"
              @change="processRepeatItemChange"
            />
          </validation-provider>

          <div
            v-if="repeatMode === 'custom'"
          >
            <v-row no-gutters>
              <v-col
                cols="2"
                class="text-body-2 grey--text text--darken-2 pt-3"
              >
                {{ $t('repeat_settings.repeat.every') }}
              </v-col>
              <v-col
                cols="2"
                class="pl-4"
              >
                <validation-provider
                  v-slot="{ errors }"
                  key="eventFormInputRepeatInterval"
                  rules="required|numeric|min_value:1|max_value:100"
                >
                  <v-text-field
                    v-model="eventForm.repeat_settings.repeat_interval"
                    dense
                    type="number"
                    min="1"
                    max="100"
                    hide-details
                    outlined
                    :disabled="!editingRules.canEditTime"
                    :error-messages="errors"
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="3"
                class="pl-2"
              >
                <v-select
                  v-model="eventForm.repeat_settings.repeat_unit"
                  dense
                  outlined
                  :disabled="!editingRules.canEditTime"
                  :items="repeatUnitItems"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="eventForm.repeat_settings.repeat_unit === 'week'"
              no-gutters
              align="center"
              class="mb-6"
            >
              <v-col
                cols="2"
                class="text-body-2 grey--text text--darken-2"
              >
                {{ $t('repeat_settings.repeat.repeat_on') }}
              </v-col>
              <v-col
                class="pl-4"
              >
                <validation-provider
                  v-slot="{ errors }"
                  key="eventFormInputRepeatWeekdays"
                  rules="required"
                >
                  <v-btn-toggle
                    v-model="eventForm.repeat_settings.constraints.weekdays"
                    dense
                    multiple
                    mandatory
                    color="primary"
                    :disabled="!editingRules.canEditTime"
                  >
                    <v-btn
                      :value="1"
                      class="text-none"
                    >
                      {{ $t('day.mon') }}
                    </v-btn>
                    <v-btn
                      :value="2"
                      class="text-none"
                    >
                      {{ $t('day.tue') }}
                    </v-btn>
                    <v-btn
                      :value="3"
                      class="text-none"
                    >
                      {{ $t('day.wed') }}
                    </v-btn>
                    <v-btn
                      :value="4"
                      class="text-none"
                    >
                      {{ $t('day.thu') }}
                    </v-btn>
                    <v-btn
                      :value="5"
                      class="text-none"
                    >
                      {{ $t('day.fri') }}
                    </v-btn>
                    <v-btn
                      :value="6"
                      class="text-none"
                    >
                      {{ $t('day.sat') }}
                    </v-btn>
                    <v-btn
                      :value="7"
                      class="text-none"
                    >
                      {{ $t('day.sun') }}
                    </v-btn>
                  </v-btn-toggle>
                  <div
                    v-if="errors"
                    class="caption error--text"
                  >
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </v-col>
            </v-row>

            <v-row
              v-if="eventForm.repeat_settings.repeat_unit === 'month'"
              no-gutters
              align="center"
              class="mb-6"
            >
              <v-col
                cols="2"
                class="text-body-2 grey--text text--darken-2"
              >
                {{ $t('dropdown.repeat.title') }}
              </v-col>
              <v-col
                class="pl-4"
              >
                <v-select
                  v-model="eventForm.repeat_settings.constraints.monthly_mode"
                  :items="monthlyModeItems"
                  dense
                  outlined
                  :disabled="!editingRules.canEditTime"
                  hide-details
                />
              </v-col>
            </v-row>
          </div>

          <div class="text-body-2 grey--text text--darken-2">
            {{ $t('repeat_settings.ends') }}
          </div>
          <v-radio-group
            v-model="eventForm.repeat_settings.end_mode"
            class="mb-2"
            :disabled="!editingRules.canEditTime"
          >
            <v-row
              no-gutters
              align="center"
              class="mb-1"
              style="height: 40px"
            >
              <v-col cols="auto">
                <v-radio
                  value="never"
                />
              </v-col>
              <v-col
                class="grey--text text--lighten-1"
                :class="{ 'text--darken-2': eventForm.repeat_settings.end_mode === 'never'}"
              >
                {{ $t('repeat_settings.repeat.never') }}
              </v-col>
            </v-row>
            <v-row
              no-gutters
              align="center"
              class="mb-1"
            >
              <v-col cols="auto">
                <v-radio
                  value="end_at"
                />
              </v-col>
              <v-col
                class="grey--text text--lighten-1"
                :class="{ 'text--darken-2': eventForm.repeat_settings.end_mode === 'end_at'}"
              >
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col cols="2">
                    {{ $t('repeat_settings.repeat.end_on') }}
                  </v-col>
                  <v-col
                    cols="4"
                    class="pl-4"
                  >
                    <v-menu
                      v-model="repeatEndAtCalendar"
                      :close-on-content-click="false"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :value="eventForm.repeat_settings.end_at ? $dayjs(eventForm.repeat_settings.end_at).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                          dense
                          outlined
                          hide-details
                          readonly
                          :disabled="eventForm.repeat_settings.end_mode !== 'end_at' || !editingRules.canEditTime"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="eventForm.repeat_settings.end_at"
                        :first-day-of-week="1"
                        no-title
                        :min="eventForm.date ? $dayjs(eventForm.date).format('YYYY-MM-DD') : undefined"
                        @input="repeatEndAtCalendar = false"
                      />
                    </v-menu>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row
              no-gutters
              align="center"
            >
              <v-col cols="auto">
                <v-radio
                  value="end_after_occurrences"
                />
              </v-col>
              <v-col
                class="grey--text text--lighten-1"
                :class="{ 'text--darken-2': eventForm.repeat_settings.end_mode === 'end_after_occurrences'}"
              >
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col cols="2">
                    {{ $t('repeat_settings.repeat.end_after') }}
                  </v-col>
                  <v-col
                    cols="4"
                    class="pl-4"
                  >
                    <v-text-field
                      v-model="eventForm.repeat_settings.end_after_occurrences"
                      dense
                      outlined
                      hide-details
                      :suffix="$t('text_input.end_after.suffix')"
                      :disabled="eventForm.repeat_settings.end_mode !== 'end_after_occurrences' || !editingRules.canEditTime"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-radio-group>
        </div>

        <div v-if="eventTypeSelector === eventTypeMatch">
          <subtitle class="mb-4">
            <v-icon
              left
              small
            >
              fal fa-futbol
            </v-icon> {{ $t('match_details.title') || 'Match Details' }}
          </subtitle>

          <v-row
            align="center"
            class="mb-2"
          >
            <v-col>
              {{ $t("switch.official_match") }}
            </v-col>
            <v-col cols="auto">
              <validation-provider
                key="eventFormInputOfficialMatch"
                rules="required"
              >
                <v-switch
                  v-model="eventForm.is_official_match"
                  hide-details
                  class="pa-0 ma-0"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <div
            v-if="eventForm.is_official_match"
            class="mb-5"
          >
            <validation-provider
              v-slot="{ errors }"
              key="eventFormInputCompetitionType"
              rules="required"
            >
              <v-select
                v-model="eventForm.competition_type_id"
                :error-messages="errors"
                :label="$t('dropdown.competition_type.title')"
                :placeholder="$t('dropdown.competition_type.placeholder')"
                :items="competitionTypes.data"
                :loading="competitionTypes.loading"
                item-value="id"
                item-text="name"
                outlined
                dense
                hide-details
                class="mb-2"
              />
            </validation-provider>
          </div>

          <v-row
            align="center"
            class="mb-2"
          >
            <v-col>
              {{ $t("switch.home_away") || "Home/Away" }}
            </v-col>
            <v-col cols="auto">
              <v-btn-toggle
                v-model="eventForm.is_home"
                mandatory
                dense
                class="elevation-0"
              >
                <v-btn
                  :value="true"
                  small
                  class="px-3"
                >
                  {{ $t("button.home") || "Home" }}
                </v-btn>
                <v-btn
                  :value="false"
                  small
                  class="px-3"
                >
                  {{ $t("button.away") || "Away" }}
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>

          <validation-provider
            v-slot="{ errors }"
            key="eventFormInputOpponent"
            rules="required"
          >
            <v-combobox
              v-model="eventForm.opponent"
              :error-messages="errors"
              :label="$t('dropdown.opponent.title')"
              :placeholder="$t('dropdown.opponent.placeholder')"
              :items="opponents.data"
              :loading="opponents.loading"
              item-value="id"
              item-text="name"
              outlined
              dense
              clearable
              class="mb-2"
              :search-input.sync="opponents.searchInput"
            />
          </validation-provider>
        </div>

        <div v-if="!hasNoPlayers && hasDiagnosticsModule && (eventForm.event_type === eventTypeTestSession || eventForm.event_type === eventTypeReadiness)">
          <v-row
            class="mb-2"
            no-gutters
          >
            <v-col>
              <subtitle>
                <v-icon
                  left
                  small
                >
                  fal fa-list-check
                </v-icon> {{ $t('add_new.diagnostic_protocol') }}
              </subtitle>
            </v-col>
            <v-col
              v-if="valdMeasurements.data.length > 0"
              cols="auto"
            >
              <v-switch
                v-model="eventForm.sync_vald_data"
                class="mt-0 pt-0"
                hide-details
                dense
              />
            </v-col>
            <v-col
              v-if="valdMeasurements.data.length > 0"
              cols="auto"
            >
              <span class="text-caption text--darken-2">
                Sync VALD data
              </span>
            </v-col>
          </v-row>

          <validation-provider
            v-slot="{ errors }"
            key="eventFormInputProtocol"
            :rules="{ 'required': eventForm.event_type === eventTypeTestSession }"
          >
            <v-autocomplete
              v-model="eventForm.diagnostic_protocol_id"
              outlined
              :items="protocol.items"
              :loading="protocol.itemsLoading"
              item-value="id"
              item-text="name"
              dense
              :error-messages="errors"
              :placeholder="$t('card_title.select_testing_protocol')"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content style="max-width: 500px">
                  <v-list-item-title>
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </validation-provider>
        </div>

        <v-row
          v-if="hasOpenEventApplications"
          no-gutters
          align="center"
          :class="{ 'mb-6': !eventForm.is_open_application, 'mb-1': eventForm.is_open_application }"
        >
          <v-col cols="auto">
            <subtitle>
              <v-icon
                left
                small
              >
                fal fa-user-plus
              </v-icon> {{ $t('eventDetails.reservations.open_application') }}
            </subtitle>
          </v-col>
          <v-col
            cols="auto"
            class="pl-4"
          >
            <v-switch
              v-model="eventForm.is_open_application"
              class="pa-0 ma-0"
              hide-details
              :disabled="!editingRules.canEditTime"
            />
          </v-col>
        </v-row>

        <div
          v-if="hasOpenEventApplications && eventForm.is_open_application"
          class="mb-8"
        >
          <div class="grey--text text-caption mb-3">
            {{ $t('eventDetails.reservations.allow_players_label') }}
          </div>

          <v-row no-gutters>
            <v-col
              cols="3"
              class="text-body-2 grey--text text--darken-2 pt-3"
            >
              {{ $t('eventDetails.reservations.max_participants') }}
            </v-col>
            <v-col
              cols="3"
            >
              <validation-provider
                v-slot="{ errors }"
                key="eventFormInputMaxParticipants"
                rules="required|numeric"
              >
                <v-text-field
                  v-model="eventForm.max_participants"
                  dense
                  type="number"
                  min="1"
                  max="1000"
                  :placeholder="$t('label.e_g') + ' ' + 100"
                  hide-details
                  outlined
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row
            class="mt-4"
            no-gutters
            align="center"
          >
            <v-col
              cols="3"
              class="text-body-2 grey--text text--darken-2"
            >
              {{ $t('eventDetails.reservations.applications_open') }}
            </v-col>
            <v-col cols="2">
              <validation-provider
                v-slot="{ errors }"
                key="eventFormInputStartDaysBefore"
                rules="numeric"
              >
                <v-text-field
                  v-model="eventForm.open_application_start_days_before"
                  dense
                  type="number"
                  min="1"
                  hide-details
                  outlined
                  :suffix="$t('suffixes.day')"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col
              cols="2"
              class="pl-1"
            >
              <validation-provider
                v-slot="{ errors }"
                key="eventFormInputStartHoursBefore"
                rules="numeric"
              >
                <v-text-field
                  v-model="eventForm.open_application_start_hours_before"
                  dense
                  type="number"
                  hide-details
                  outlined
                  :suffix="$t('suffixes.hour')"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col
              cols="auto"
              class="text-body-2 grey--text text--darken-2 pl-3"
            >
              {{ $t('eventDetails.reservations.before') }}
            </v-col>
            <v-col
              v-if="openApplicationStartAt"
              class="grey--text caption pl-3 text-right"
            >
              {{ $dayjs(openApplicationStartAt).format(getDateTimeFormatForUser('ddd, MMM D, HH:mm')) }}
            </v-col>
          </v-row>

          <v-row
            class="mt-4"
            no-gutters
            align="center"
          >
            <v-col
              cols="3"
              class="text-body-2 grey--text text--darken-2"
            >
              {{ $t("eventDetails.reservations.applications_close") }}
            </v-col>
            <v-col cols="2">
              <validation-provider
                v-slot="{ errors }"
                key="eventFormInputEndDaysBefore"
                rules="numeric"
              >
                <v-text-field
                  v-model="eventForm.open_application_end_days_before"
                  dense
                  type="number"
                  min="1"
                  hide-details
                  outlined
                  :suffix="$t('suffixes.day')"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col
              cols="2"
              class="pl-1"
            >
              <validation-provider
                v-slot="{ errors }"
                key="eventFormInputEndHoursBefore"
                rules="numeric"
              >
                <v-text-field
                  v-model="eventForm.open_application_end_hours_before"
                  dense
                  type="number"
                  hide-details
                  outlined
                  :suffix="$t('suffixes.hour')"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col
              cols="auto"
              class="text-body-2 grey--text text--darken-2 pl-3"
            >
              {{ $t('eventDetails.reservations.before') }}
            </v-col>
            <v-col
              v-if="openApplicationEndAt"
              class="grey--text caption pl-3 text-right"
            >
              {{ $dayjs(openApplicationEndAt).format(getDateTimeFormatForUser('ddd, MMM D, HH:mm')) }}
            </v-col>
          </v-row>

          <v-row
            v-if="!hasNoPlayers"
            class="mt-4"
            no-gutters
            align="center"
          >
            <v-col
              cols="6"
              class="text-body-2 grey--text text--darken-2"
            >
              {{ $t("eventDetails.reservations.limit_applications") }}
            </v-col>
            <v-col>
              <v-switch
                v-model="eventForm.is_open_application_team_constrained"
                class="pa-0 ma-0"
                hide-details
                dense
              />
            </v-col>
          </v-row>
        </div>

        <div v-if="!hasNoPlayers">
          <subtitle class="mb-3">
            <v-icon
              left
              small
            >
              fal fa-user-group
            </v-icon> {{ $t('add_new.team') }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
            key="eventFormInputTeams"
            :rules="{ required: !!eventForm.is_open_application_team_constrained }"
          >
            <v-autocomplete
              v-model="eventForm.team_ids"
              outlined
              :search-input.sync="searchInputTeams"
              :items="teamSelectItems"
              item-value="id"
              item-text="name"
              dense
              multiple
              :disabled="!editingRules.canEditTeamsAndPlayers"
              :error-messages="errors"
              :placeholder="$t('select_team.placeholder')"
            >
              <template v-slot:selection="{item}">
                <span class="d-inline-flex align-center mr-1"><team-color-dot :color="item.color" />{{ item.name }}</span>
              </template>
              <template v-slot:item="{item}">
                <team-color-dot :color="item.color" />{{ item.name }}
              </template>
            </v-autocomplete>
          </validation-provider>

          <v-select
            v-if="hasDosbFilter && dosbAttribute"
            v-model="dosbFilterValues"
            :items="dosbOptions"
            :label="dosbAttributeLabel"
            outlined
            dense
            multiple
            chips
            small-chips
            clearable
            :disabled="!editingRules.canEditTeamsAndPlayers"
          />

          <v-select
            v-if="isFederation && playerGroups.data.length > 0"
            v-model="eventForm.player_group_id"
            :items="playerGroups.data"
            :loading="playerGroups.loading"
            item-text="name"
            item-value="id"
            :label="$t('dropdown.player_group.title')"
            :placeholder="$t('dropdown.player_group.placeholder')"
            outlined
            dense
            clearable
            :disabled="!editingRules.canEditTeamsAndPlayers"
          />

          <v-row
            no-gutters
            class="mb-3"
            align="center"
          >
            <v-col>
              <subtitle>
                <v-icon
                  left
                  small
                >
                  fal fa-user
                </v-icon>
                {{ $t("dropdown.players.title") }}
              </subtitle>
            </v-col>
            <v-col
              class="grey--text text-caption"
              cols="auto"
            >
              {{ $t('added_players', {count: eventForm.player_ids.length}) }}
            </v-col>
          </v-row>

          <validation-provider
            v-slot="{ errors }"
            key="eventFormInputPlayers"
            :rules="{ required: false, max_selections: editingRules.onlyOnePlayer ? { max: 1 } : false }"
          >
            <v-autocomplete
              v-model="eventForm.player_ids"
              outlined
              :search-input.sync="searchInputPlayers"
              :items="playersToSelect"
              item-value="id"
              item-text="name"
              dense
              multiple
              :chips="false"
              :error-messages="errors"
              clearable
              :disabled="!editingRules.canEditTeamsAndPlayers"
              :placeholder="$t('dropdown.players.placeholder')"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="index > 0 && index < 2"
                  class="mr-1"
                >, </span>
                <span v-if="index >= 0 && index < 2">{{ item.first_name }} {{ item.last_name }}</span>
                <span
                  v-else-if="index === 2"
                  class="grey--text text-caption ml-1"
                >
                  {{ $t('event.and_others') }}
                </span>
              </template>
              <template v-slot:item="{ item, on }">
                <v-list-item-action>
                  <v-simple-checkbox
                    :value="eventForm.player_ids.indexOf(item.id) > -1"
                    :ripple="false"
                    v-on="on"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.last_name }}, {{ item.first_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="item.teams && item.teams.length">{{ item.teams.map(t => t.short_name).join(', ') }}</span>
                    <span
                      v-if="item.player_positions && item.player_positions.length > 0"
                    > &middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </validation-provider>
        </div>

        <v-switch
          v-if="!hasNoPlayers && (eventForm.event_type === eventTypeTestSession || eventForm.event_type === eventTypeReadiness)"
          v-model="eventForm.is_player_diagnostic_entry_allowed"
          class="pa-0 ma-0 mb-6"
          hide-details
          dense
          :label="$t('player_enter_diagnostic_results.label')"
        />

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user
          </v-icon> {{ $t("eventDetails.reservations.coaches") }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="eventFormInputCoaches"
          :rules="{ required: !!eventForm.is_open_application }"
        >
          <v-autocomplete
            v-model="eventForm.coach_ids"
            outlined
            :search-input.sync="searchInputCoaches"
            :items="coaches.data"
            :loading="coaches.loading"
            item-value="id"
            :item-text="(coach) => coach.first_name + ' ' + coach.last_name"
            dense
            multiple
            clearable
            :disabled="!editingRules.canEditTeamsAndPlayers"
            :error-messages="errors"
            :placeholder="$t('eventDetails.reservations.coaches.placeholder')"
          >
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 3"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 3">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 3"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
          </v-autocomplete>
        </validation-provider>

        <div v-if="hasTrainingBuilder && $can('view-training-protocols')">
          <div
            v-if="eventForm.event_type === eventTypeTraining || eventForm.event_type === eventTypeMatch || eventForm.event_type === eventTypeReadiness || eventForm.event_type === eventTypeRehab || eventForm.event_type === eventTypeIndividualWork"
            class="mb-6"
          >
            <v-row
              class="mb-0"
              align="center"
            >
              <v-col>
                <subtitle>
                  <v-icon
                    left
                    small
                  >
                    fal fa-list-check
                  </v-icon>
                  {{ $t('add_new.training') }}
                </subtitle>
              </v-col>
              <v-col
                v-if="trainingProtocolItems.length === 0 ? $can('create-training-protocols') : $can('edit-training-protocols')"
                cols="auto"
                class="pl-4"
              >
                <v-btn
                  text
                  small
                  color="primary"
                  class="text-none"
                  @click="trainingProtocolDialog = true"
                >
                  <v-icon
                    left
                    x-small
                  >
                    far fa-pen
                  </v-icon>
                  {{ trainingProtocolItems.length === 0 ? $t('button.create_protocol') : $t('button.adjust_protocol') }}
                </v-btn>
              </v-col>
            </v-row>

            <validation-provider
              v-slot="{ errors }"
              key="eventFormInputTrainingProtocol"
              :rules="{ required: false }"
            >
              <v-autocomplete
                v-model="trainingProtocol.selected"
                outlined
                :items="trainingProtocol.items"
                :loading="trainingProtocol.itemsLoading"
                item-value="id"
                item-text="name"
                dense
                :error-messages="errors"
                clearable
                return-object
                hide-details
                :menu-props="{ maxWidth: '540px' }"
                :placeholder="$t('dropdown.training_protocol.placeholder')"
                @change="loadTrainingProtocol"
                @click:clear="trainingProtocolItems = []; trainingProtocol.selected = null"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ item.description }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </validation-provider>

            <div
              v-if="trainingProtocolItems.length > 0"
              class="mt-3"
            >
              <div
                v-if="trainingProtocol.selected"
                class="mb-3 text-body-1 text--darken-2"
              >
                {{ trainingProtocol.selected.description }}
              </div>

              <div
                v-for="item in trainingProtocolItems"
                :key="item.id"
                class="mb-1"
              >
                <superset-selected-card
                  v-if="item.type === 'superset'"
                  dense
                  :orderable="false"
                  :editable="false"
                  :item.sync="item"
                />
                <group-selected-card
                  v-else-if="item.type === 'group'"
                  dense
                  :orderable="false"
                  :editable="false"
                  :item.sync="item"
                />
                <pause-selected-card
                  v-else-if="item.type === 'pause'"
                  dense
                  :orderable="false"
                  :editable="false"
                  :item.sync="item"
                />
                <exercise-selected-card-template
                  v-else-if="item.group.group_id"
                  dense
                  :orderable="false"
                  :editable="false"
                  :exercise="item"
                />
              </div>
            </div>
          </div>

          <v-switch
            v-if="trainingProtocol.selected"
            v-model="eventForm.player_can_view_training_protocol"
            class="pa-0 ma-0 mb-6"
            hide-details
            dense
            :label="$t('switch.training_protocol.players_allowed')"
          />
        </div>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-location-dot
          </v-icon> {{ $t('dropdown.location.title') }}
        </subtitle>

        <location-autocomplete
          v-model="eventForm.location"
          :locations="location.items"
          :loading="location.loading"
          outlined
          dense
          :placeholder="$t('dropdown.location.placeholder')"
          @input="onLocationSelected"
        />

        <div v-if="eventForm.event_type === eventTypeTravel">
          <subtitle class="mb-3">
            <v-icon
              left
              small
            >
              fal fa-bus
            </v-icon> {{ $t("event_details.means_of_travel") }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-select
              v-model="eventForm.travel_means"
              outlined
              :items="travelMeansItems"
              dense
              :error-messages="errors"
              :placeholder="$t('dropdown.select_means_of_travel.placeholder')"
            />
          </validation-provider>
        </div>

        <div
          v-if="eventForm.event_type === eventTypeMassage"
          class="mb-6"
        >
          <subtitle class="mb-3">
            <v-icon
              left
              small
            >
              fal fa-hand-dots
            </v-icon> {{ $t("event_details.massage_details") }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-select
              v-model="eventForm.massage_treatment_type_id"
              outlined
              :items="massageTreatmentTypes.data"
              :loading="massageTreatmentTypes.loading"
              item-value="id"
              item-text="name"
              dense
              :error-messages="errors"
              :placeholder="$t('dropdown.select_massage_treatment_type.placeholder')"
            />
          </validation-provider>

          <validation-provider>
            <v-row>
              <v-col class="justify-center d-flex">
                <pain-avatar-group
                  interactive
                  :width="210"
                  :height="400"
                  :player-stats="massageStats"
                  stats-suffix=""
                  @click="massageMuscleToggle"
                />
              </v-col>
            </v-row>
          </validation-provider>
        </div>

        <div
          v-if="!eventForm.id && $can('create-event-notes')"
          class="mb-6"
        >
          <v-row class="mb-0">
            <v-col>
              <subtitle>
                <v-icon
                  left
                  small
                >
                  fal fa-list
                </v-icon> {{ $t('add_note.dialog.header') }}
              </subtitle>
            </v-col>
            <v-col
              v-if="!hasNoPlayers"
              cols="auto"
              class="pl-4"
            >
              <v-switch
                v-model="eventForm.is_note_pinned"
                class="pa-0 ma-0"
                hide-details
                dense
                :label="$t('switch.pinned')"
              />
            </v-col>
            <v-col
              v-if="!hasNoPlayers"
              cols="auto"
              class="pl-4"
            >
              <v-switch
                v-model="eventForm.is_description_players_visible"
                class="pa-0 ma-0"
                hide-details
                dense
                :label="$t('switch.visible_to_players')"
              />
            </v-col>
          </v-row>

          <div>
            <rich-text-editor v-model="eventForm.description" />
          </div>
        </div>

        <div v-if="activitySourceItems.length > 0 && [eventTypeTraining, eventTypeMatch, eventTypeReadiness, eventTypeRehab, eventTypeIndividualWork].indexOf(eventForm.event_type) > -1 && $can('create-performance')">
          <div class="text-subtitle-1 grey--text text--darken-3 mb-3">
            {{ $t('text_input.integrations.title') }}
          </div>

          <div v-if="activitySourceItems.length > 1">
            <v-row class="mb-0">
              <v-col>
                <v-select
                  v-model="eventForm.activity_source"
                  :items="activitySourceItems"
                  item-value="value"
                  item-text="text"
                  outlined
                  dense
                  clearable
                  :label="$t('dropdown.activity_source.label')"
                />
              </v-col>
            </v-row>
          </div>

          <div v-if="hasCatapultIntegration && (!eventForm.activity_source || eventForm.activity_source === 'CATAPULT')">
            <v-row class="mb-0">
              <v-col>
                <validation-provider
                  key="eventFormInputCatapult"
                  v-slot="{ errors }"
                  name="catapult_id"
                  mode="passive"
                  :rules="{ single_integration: ['catapult_id', ['gpexe_id', 'polar_team_id', 'kinexon_id', 'statsports_api_id', 'wimu_id'], eventForm] }"
                >
                  <v-autocomplete
                    v-model="eventForm.catapult_id"
                    :items="externalEvents.catapult.data"
                    :loading="externalEvents.catapult.loading"
                    outlined
                    :label="$t('dropdown.integrations.catapult.placeholder')"
                    item-value="external_id"
                    item-text="title"
                    dense
                    clearable
                    :error-messages="errors"
                    :menu-props="{ maxWidth: !hasCatapultAutosync && hasCatapultEvent ? '415px' : undefined }"
                  >
                    <template v-slot:item="{ item: externalEvent }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ externalEvent.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <strong>{{ $dayjs(externalEvent.start_at).format(getDateTimeFormatForUser('ll HH:mm')) }} - {{ $dayjs(externalEvent.end_at).format('HH:mm') }}</strong> &middot; {{ $tc("players.count", externalEvent.players.length, {count: externalEvent.players.length}) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col
                v-if="!hasCatapultAutosync && hasCatapultEvent"
                cols="auto"
              >
                <v-switch
                  v-model="eventForm.resync_catapult_data"
                  class="mt-1"
                  :label="$t('switch.resync')"
                />
              </v-col>
            </v-row>
          </div>

          <div v-if="hasGpexeIntegration && (!eventForm.activity_source || eventForm.activity_source === 'GPEXE')">
            <v-row class="mb-0">
              <v-col>
                <validation-provider
                  key="eventFormInputGpexe"
                  v-slot="{ errors }"
                  name="gpexe_id"
                  mode="passive"
                  :rules="{ single_integration: ['gpexe_id', ['catapult_id', 'polar_team_id', 'kinexon_id', 'statsports_api_id', 'wimu_id'], eventForm] }"
                >
                  <v-autocomplete
                    v-model="eventForm.gpexe_id"
                    :items="externalEvents.gpexe.data"
                    :loading="externalEvents.gpexe.loading"
                    outlined
                    :label="$t('dropdown.integrations.gpexe.placeholder')"
                    item-value="external_id"
                    item-text="title"
                    dense
                    clearable
                    :error-messages="errors"
                    :menu-props="{ maxWidth: !hasGpexeAutosync && hasGpexeEvent ? '415px' : undefined }"
                  >
                    <template v-slot:item="{ item: externalEvent }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ externalEvent.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <strong>{{ $dayjs(externalEvent.start_at).format('ll HH:mm') }} - {{ $dayjs(externalEvent.end_at).format('HH:mm') }}</strong>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col
                v-if="!hasGpexeAutosync && hasGpexeEvent"
                cols="auto"
              >
                <v-switch
                  v-model="eventForm.resync_gpexe_data"
                  class="mt-1"
                  :label="$t('switch.resync')"
                />
              </v-col>
            </v-row>
          </div>

          <div v-if="hasPolarTeamIntegration && (!eventForm.activity_source || eventForm.activity_source === 'POLAR_TEAM')">
            <v-row class="mb-0">
              <v-col>
                <validation-provider
                  key="eventFormInputPolarTeam"
                  v-slot="{ errors }"
                  name="polar_team_id"
                  mode="passive"
                  :rules="{ single_integration: ['polar_team_id', ['catapult_id', 'gpexe_id', 'kinexon_id', 'statsports_api_id', 'wimu_id'], eventForm] }"
                >
                  <v-autocomplete
                    v-model="eventForm.polar_team_id"
                    :items="externalEvents.polarTeam.data"
                    :loading="externalEvents.polarTeam.loading"
                    outlined
                    :label="$t('dropdown.integrations.polar_team.placeholder')"
                    item-value="external_id"
                    :item-text="(external) => external.title ? external.title : external.external_id"
                    dense
                    clearable
                    :error-messages="errors"
                    :menu-props="{ maxWidth: !hasPolarTeamAutosync && hasPolarTeamEvent ? '415px' : undefined }"
                  >
                    <template v-slot:item="{ item: externalEvent }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ externalEvent.external_id }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <strong>{{ $dayjs(externalEvent.start_at).format('ll HH:mm') }} - {{ $dayjs(externalEvent.end_at).format('HH:mm') }}</strong> &middot; {{ $tc("players.count", externalEvent.players.length, {count: externalEvent.players.length}) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col
                v-if="!hasPolarTeamAutosync && hasPolarTeamEvent"
                cols="auto"
              >
                <v-switch
                  v-model="eventForm.resync_polar_team_data"
                  class="mt-1"
                  :label="$t('switch.resync')"
                />
              </v-col>
            </v-row>
          </div>

          <div v-if="hasKinexonIntegration && (!eventForm.activity_source || eventForm.activity_source === 'KINEXON')">
            <v-row class="mb-0">
              <v-col>
                <validation-provider
                  key="eventFormInputKinexon"
                  v-slot="{ errors }"
                  name="kinexon_id"
                  mode="passive"
                  :rules="{ single_integration: ['kinexon_id', ['catapult_id', 'gpexe_id', 'polar_team_id', 'statsports_api_id', 'wimu_id'], eventForm] }"
                >
                  <v-autocomplete
                    v-model="eventForm.kinexon_id"
                    :items="externalEvents.kinexon.data"
                    :loading="externalEvents.kinexon.loading"
                    outlined
                    :label="$t('dropdown.integrations.kinexon.placeholder')"
                    item-value="external_id"
                    :item-text="(external) => external.title ? external.title : external.external_id"
                    dense
                    clearable
                    :error-messages="errors"
                  >
                    <template v-slot:item="{ item: externalEvent }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ externalEvent.external_id }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <strong>{{ $dayjs(externalEvent.start_at).format('ll HH:mm') }} - {{ $dayjs(externalEvent.end_at).format('HH:mm') }}</strong> &middot; {{ $tc("players.count", externalEvent.players.length, {count: externalEvent.players.length}) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
            </v-row>
          </div>

          <div v-if="hasStatsportsApiIntegration && (!eventForm.activity_source || eventForm.activity_source === 'STATSPORTS')">
            <v-row>
              <v-col>
                <validation-provider
                  key="eventFormInputStatsportsApi"
                  v-slot="{ errors }"
                  name="statsports_api_id"
                  mode="passive"
                  :rules="{ single_integration: ['statsports_api_id', ['catapult_id', 'gpexe_id', 'polar_team_id', 'kinexon_id', 'wimu_id'], eventForm] }"
                >
                  <v-autocomplete
                    v-model="eventForm.statsports_api_id"
                    :items="externalEvents.statsportsApi.data"
                    :loading="externalEvents.statsportsApi.loading"
                    outlined
                    :label="$t('dropdown.integrations.statsports_api.placeholder')"
                    item-value="external_id"
                    item-text="title"
                    dense
                    clearable
                    :error-messages="errors"
                    :menu-props="{ maxWidth: hasStatsportsApiEvent ? '415px' : undefined }"
                  >
                    <template v-slot:item="{ item: externalEvent }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ externalEvent.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <strong>
                            {{ $dayjs(externalEvent.start_at).add($dayjs(externalEvent.start_at).utcOffset(), 'minute').format(getDateTimeFormatForUser('ll HH:mm')) }} -
                            {{ $dayjs(externalEvent.end_at).add($dayjs(externalEvent.end_at).utcOffset(), 'minute').format('HH:mm') }}
                          </strong>
                          &middot; {{ $tc("players.count", externalEvent.players_length, {count: externalEvent.players_length}) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col
                v-if="hasStatsportsApiEvent"
                cols="auto"
              >
                <v-switch
                  v-model="eventForm.resync_statsports_api_data"
                  class="mt-1"
                  :label="$t('switch.resync')"
                />
              </v-col>
            </v-row>
          </div>

          <div v-if="hasWimuIntegration && (!eventForm.activity_source || eventForm.activity_source === 'WIMU')">
            <v-row>
              <v-col>
                <validation-provider
                  key="eventFormInputWimu"
                  v-slot="{ errors }"
                  name="wimu_id"
                  mode="passive"
                  :rules="{ single_integration: ['wimu_id', ['catapult_id', 'gpexe_id', 'polar_team_id', 'kinexon_id', 'statsports_api_id'], eventForm] }"
                >
                  <v-autocomplete
                    v-model="eventForm.wimu_id"
                    :items="externalEvents.wimu.data"
                    :loading="externalEvents.wimu.loading"
                    outlined
                    :label="$t('dropdown.integrations.wimu.placeholder')"
                    item-value="external_id"
                    item-text="title"
                    dense
                    clearable
                    :error-messages="errors"
                    :menu-props="{ maxWidth: hasWimuEvent ? '415px' : undefined }"
                  >
                    <template v-slot:item="{ item: externalEvent }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ externalEvent.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <strong>{{ $dayjs(externalEvent.start_at).format(getDateTimeFormatForUser('ll HH:mm')) }} - {{ $dayjs(externalEvent.end_at).format('HH:mm') }}</strong>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col
                v-if="hasWimuEvent"
                cols="auto"
              >
                <v-switch
                  v-model="eventForm.resync_wimu_data"
                  class="mt-1"
                  :label="$t('switch.resync')"
                />
              </v-col>
            </v-row>
          </div>

          <div v-if="hasCometIntegration && eventForm.event_type === eventTypeMatch && (!eventForm.activity_source || eventForm.activity_source === 'COMET')">
            <v-row class="mb-0">
              <v-col>
                <validation-provider
                  key="eventFormInputComet"
                  v-slot="{ errors }"
                  name="comet_id"
                  mode="passive"
                >
                  <v-autocomplete
                    v-model="eventForm.comet_id"
                    :items="filteredCometMatches"
                    :loading="externalEvents.comet.loading"
                    outlined
                    :label="$t('dropdown.integrations.comet.placeholder')"
                    item-value="id"
                    dense
                    clearable
                    :error-messages="errors"
                    :menu-props="{ maxWidth: eventForm.comet_id && eventForm.id ? '415px' : '537px' }"
                  >
                    <template v-slot:item="{ item: match }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ match.home_team }} vs {{ match.away_team }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <strong>{{ $dayjs(match.date_time).format('ll HH:mm') }}</strong> &middot; {{ match.competition.name }} &middot; {{ match.facility }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item: match }">
                      {{ match.home_team }} vs {{ match.away_team }}
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col
                v-if="eventForm.comet_id && eventForm.id"
                cols="auto"
              >
                <v-switch
                  v-model="eventForm.resync_comet_data"
                  class="mt-1"
                  :label="$t('switch.resync')"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <v-divider
          v-if="$can('view-event-media') || $can('create-event-media')"
          class="mb-6"
        />

        <subtitle
          v-if="$can('view-event-media') || $can('create-event-media')"
          class="mb-3"
        >
          <v-icon
            left
            small
          >
            fal fa-photo-video
          </v-icon> {{ $t('event_details.media') }}
        </subtitle>

        <div v-if="$can('create-event-media')">
          <v-overlay
            color="white"
            light
            :value="fileUpload.isDragging"
            :opacity="0.8"
            @dragleave.prevent="dragleave"
            @dragover.prevent="dragover"
            @drop.prevent="drop"
          >
            <div
              class="mb-4 text-center"
              style="pointer-events: none"
            >
              <v-icon
                x-large
                color="grey darken-4"
              >
                fal fa-upload
              </v-icon>
            </div>
            <div
              class="grey--text text--darken-4"
              style="pointer-events: none"
            >
              {{ $t('drag_n_drop.instruction') }}
            </div>
          </v-overlay>

          <v-row
            align="center"
            class="mb-4"
          >
            <v-col class="text-body-2 grey--text">
              {{ $t('media_upload.instruction') }}
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="grey darken-2"
                text
                class="text-none"
                @click="$refs.uploadFile.click()"
              >
                {{ $t('button.browse') }}
                <v-icon
                  right
                  small
                >
                  fal fa-upload
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <input
            id="upload-file"
            ref="uploadFile"
            type="file"
            style="display: none"
            accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf"
            @input="handleFileChange"
          >
        </div>

        <div v-if="$can('view-event-media')">
          <v-row>
            <v-col
              v-for="(mediaItem, mediaItemIndex) in media"
              :key="mediaItem.id"
              cols="6"
            >
              <media-grid-item
                :media-item="mediaItem"
                class="media-grid-item"
                :can-delete="$can('delete-event-media')"
                @preview="preview"
                @delete="mediaItem.id ? startMediaDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
              />
            </v-col>
          </v-row>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            color="primary"
            block
            large
            :loading="submitting"
            @click="submit()"
          >
            {{ $t('training_builder.button.save') }}
          </v-btn>
          <v-btn
            v-if="eventForm.id && $can('delete-event')"
            class="mt-2"
            color="error"
            text
            block
            :disabled="!editingRules.canDelete"
            @click="initDelete()"
          >
            {{ $t('button.delete') }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteEvent()"
    />

    <v-dialog
      v-model="repeatEditDialog.visible"
      max-width="400px"
    >
      <v-card>
        <v-card-title class="text-body-2 px-5 py-4 grey--text text--darken-2">
          {{ $t('what_to_edit.dialog.header', { action: repeatEditDialog.deleting ? $t('button.delete').toLowerCase() : $t('button.edit').toLowerCase() }) }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <v-list
            dense
            class="pa-0"
          >
            <v-list-item-group
              v-model="repeatEditDialog.mode"
              color="primary"
              mandatory
            >
              <v-list-item
                value="all"
                input-value="all"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-1">
                    {{ $t('what_to_edit.this_and_all_future') }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="repeatEditDialog.mode === 'all'">
                  <v-icon small>
                    far fa-check
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-list-item
                value="this"
                input-value="this"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-1">
                    {{ $t('what_to_edit.only_this') }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="repeatEditDialog.mode === 'this'">
                  <v-icon small>
                    far fa-check
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="submitting || deleting"
            @click="submitRepeatEditMode()"
          >
            {{ $t('button.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <training-protocol-form-dialog
      v-if="modalVisible"
      :visible.sync="trainingProtocolDialog"
      :edit-items="trainingProtocolItems"
      :listen-events="false"
      :selectable-players="selectedPlayersForProtocol"
      :edit-title="trainingProtocol.selected ? trainingProtocol.selected.name : undefined"
      :edit-description="trainingProtocol.selected ? trainingProtocol.selected.description : undefined"
      @update:edit-items="updateTrainingProtocolItems"
    />

    <media-viewer-dialog
      :visible.sync="mediaViewerDialog.visible"
      :media="media"
      :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
      :view-only="!$can('edit-event-media')"
      :can-delete="$can('delete-event-media')"
      @media:updated="media.splice(media.findIndex(m => m.id === $event.id), 1, $event)"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteMedia.dialog"
      :submitting="deleteMedia.deleting"
      @confirmed="destroyMedia()"
    />
  </card-dialog>
</template>

<script>
import i18n from '@/plugins/i18n'
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import LocationAutocomplete from '@/components/LocationAutocomplete.vue'
import {
  CoachApiService,
  CompetitionTypeApiService,
  DiagnosticProtocolApiService,
  EventApiService,
  LocationApiService,
  MatchOpponentApiService,
  PlayerApiService,
  PlayerGroupApiService
} from '@/data/services'
import { AdditionalAttributeApiService } from '@/data/services/AdditionalAttributeApiService'
import { EventBus } from '@/plugins/bus'
import { cloneDeep, difference, intersection, union } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle'
import dayjs from 'dayjs'
import { EventType, EventTypes, TravelMeansList } from '@/data/models'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import RichTextEditor from '@/components/inputs/RichTextEditor.vue'
import ColorSwatchPicker from '@/components/inputs/ColorSwatchPicker.vue'
import TeamColorDot from '@/components/inputs/TeamColorDot.vue'
import timezones from 'timezones-list'
import { TrainingProtocolApiService } from '@/data/services/TrainingProtocolApiService'
import SupersetSelectedCard from '@/components/training-builder/SupersetSelectedCard.vue'
import PauseSelectedCard from '@/components/training-builder/PauseSelectedCard.vue'
import GroupSelectedCard from '@/components/training-builder/GroupSelectedCard.vue'
import TrainingProtocolFormDialog from '@/components/dialogs/TrainingProtocolFormDialog.vue'
import ExerciseSelectedCardTemplate from '@/components/training-builder/ExerciseSelectedCardTemplate.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import _axios from '@/plugins/axios'
import { MediaApiService } from '@/data/services/MediaApiService'
import { getDateTimeFormatForUser, getOrdinal } from '@/utils/helpers'
import { IntegrationType } from '@/data/models/Integration'
import { ExternalEventApiService } from '@/data/services/ExternalEventApiService'
import debounce from 'lodash/debounce'
import { ValdMeasurementApiService } from '@/data/services/ValdMeasurementApiService'
import { CometApiService } from '@/data/services/CometApiService'
import PainAvatarGroup from '@/components/helpers/PainAvatarGroup.vue'

const PARTICIPANT_LEVELS = ['club', 'regional_department', 'national']

const FIELD_HOCKEY_TRAINING_TYPES = [
  { value: 'hockey', methods: ['team', 'technical', 'corner'] },
  { value: 'endurance', methods: ['hit', 'lit'] },
  { value: 'strength', methods: [] },
  { value: 'prehab', methods: ['mobility', 'stability', 'coordination'] },
  { value: 'rehab', methods: [] },
  { value: 'recovery', methods: [] },
  { value: 'speed', methods: [] }
]

// Maps organisation integration key -> backend Integration enum value used for events.activity_source.
// Order here drives the order of items in the activity source selector.
const ACTIVITY_SOURCE_OPTIONS = [
  { key: 'catapult', value: 'CATAPULT', text: 'Catapult' },
  { key: 'gpexe', value: 'GPEXE', text: 'GPEXE' },
  { key: 'polar', value: 'POLAR', text: 'Polar' },
  { key: 'polar-team', value: 'POLAR_TEAM', text: 'Polar Team' },
  { key: 'garmin', value: 'GARMIN', text: 'Garmin' },
  { key: 'whoop', value: 'WHOOP', text: 'Whoop' },
  { key: 'kinexon', value: 'KINEXON', text: 'Kinexon' },
  { key: 'fieldwiz', value: 'FIELD_WIZ', text: 'Fieldwiz' },
  { key: 'comet', value: 'COMET', text: 'Comet' },
  { key: 'statsports_api', value: 'STATSPORTS', text: 'STATSports' },
  { key: 'wimu', value: 'WIMU', text: 'WIMU' }
]

const emptyForm = {
  date: dayjs().format('YYYY-MM-DD'),
  all_day: false,
  start_at: '',
  end_at: '',
  tz: null,
  title: '',
  description: '',
  is_description_players_visible: false,
  is_note_pinned: false,
  is_player_diagnostic_entry_allowed: false,
  player_can_view_training_protocol: true,
  event_type: EventType.TRAINING,
  color_override: null,
  is_official_match: false,
  training_type: null,
  training_method: null,
  participant_level: null,
  player_group_id: null,
  competition_type_id: null,
  is_home: true,
  opponent: null,
  team_ids: [],
  player_ids: [],
  coach_ids: [],
  is_open_application: false,
  max_participants: null,
  is_open_application_team_constrained: true,
  open_application_start_days_before: null,
  open_application_start_hours_before: null,
  open_application_end_days_before: null,
  open_application_end_hours_before: null,
  open_application_start_at: null,
  open_application_end_at: null,
  travel_means: null,
  location_id: null,
  diagnostic_protocol_id: null,
  sync_vald_data: false,
  training_protocol_id: null,
  external_id: null,
  catapult_id: null,
  resync_catapult_data: false,
  gpexe_id: null,
  resync_gpexe_data: false,
  polar_team_id: null,
  resync_polar_team_data: false,
  kinexon_id: null,
  comet_id: null,
  resync_comet_data: false,
  statsports_api_id: null,
  resync_statsports_api_data: false,
  wimu_id: null,
  resync_wimu_data: false,
  polar_id: null,
  activity_source: null,
  repeats: false,
  repeat_settings: {
    repeat_unit: 'week',
    repeat_interval: 1,
    constraints: {
      weekdays: [],
      monthly_mode: 'weekday'
    },
    end_mode: 'never',
    end_after_occurrences: 12,
    end_at: null
  },
  massage_treatment_type_id: null,
  massage_body_parts: []
}

export default {
  name: 'EventFormDialog',
  components: { LocationAutocomplete, PainAvatarGroup, MediaGridItem, MediaViewerDialog, ExerciseSelectedCardTemplate, TrainingProtocolFormDialog, GroupSelectedCard, PauseSelectedCard, SupersetSelectedCard, RichTextEditor, ColorSwatchPicker, TeamColorDot, DeleteConfirmationDialog, Subtitle, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    event: {
      type: Object,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dateActive: false,
      eventForm: cloneDeep(emptyForm),
      eventTypeSelector: EventType.TRAINING,
      datePickerMenuVisible: false,
      submitting: false,
      searchInputTeams: '',
      searchInputPlayers: '',
      searchInputCoaches: '',
      competitionTypes: {
        data: [],
        loading: false
      },
      opponents: {
        data: [],
        loading: false,
        searchInput: null
      },
      additionalAttributes: {
        data: [],
        loading: false
      },
      playerGroups: {
        data: [],
        loading: false
      },
      dosbFilterValues: [],
      players: {
        data: [],
        loading: false,
        eventPlayers: []
      },
      coaches: {
        data: [],
        loading: false
      },
      location: {
        items: [],
        itemsLoading: false,
        searchInput: null
      },
      protocol: {
        items: [],
        itemsLoading: false
      },
      trainingProtocol: {
        items: [],
        itemsLoading: false,
        selected: null
      },
      trainingProtocolItems: [],
      trainingProtocolDialog: false,
      externalEvents: {
        catapult: {
          data: [],
          loading: false
        },
        gpexe: {
          data: [],
          loading: false
        },
        polarTeam: {
          data: [],
          loading: false
        },
        kinexon: {
          data: [],
          loading: false
        },
        comet: {
          data: [],
          loading: false
        },
        statsportsApi: {
          data: [],
          loading: false
        },
        wimu: {
          data: [],
          loading: false
        }
      },
      valdMeasurements: {
        data: [],
        loading: false
      },
      repeatMode: 'weekly',
      repeatEndAtCalendar: false,
      repeatEditDialog: {
        visible: false,
        mode: 'all',
        deleting: false
      },
      fileUpload: {
        isDragging: false,
        isUploading: false
      },
      media: [],
      mediaViewerDialog: {
        visible: false,
        media: undefined
      },
      deleteMedia: {
        media: undefined,
        dialog: false,
        deleting: false
      },
      hasCatapultEvent: false,
      hasGpexeEvent: false,
      hasPolarTeamEvent: false,
      hasStatsportsApiEvent: false,
      hasWimuEvent: false,
      titleAutoFilled: false,
      massageTreatmentTypes: {
        data: [],
        loading: false
      }
    }
  },
  computed: {
    // Effective colour shown in the picker preview: the per-event override if set,
    // otherwise the default colour for the selected event type.
    eventColorPreview() {
      if (this.eventForm.color_override) return this.eventForm.color_override
      const type = EventTypes[this.eventForm.event_type]
      return (type && type.color) || EventTypes[EventType.OTHER].color
    },
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    teamSelectItems() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    isFieldHockey() {
      return this.user?.sport?.name === 'Field Hockey'
    },
    isFederation() {
      return this.user?.organisation_settings?.has_multi_team_players || false
    },
    participantLevelOptions() {
      return PARTICIPANT_LEVELS.map(l => ({
        value: l,
        text: this.$t(`event_group.participant_level.${l}`)
      }))
    },
    fieldHockeyTrainingTypeOptions() {
      return FIELD_HOCKEY_TRAINING_TYPES.map(t => ({
        value: t.value,
        text: this.$t(`training_protocol.type.${t.value}`)
      }))
    },
    fieldHockeyTrainingMethodOptions() {
      const t = FIELD_HOCKEY_TRAINING_TYPES.find(tt => tt.value === this.eventForm.training_type)
      if (!t) return []
      return t.methods.map(m => ({
        value: m,
        text: this.$t(`training_protocol.method.${m}`)
      }))
    },
    hasTimezoneManagement() {
      return this.user.organisation_settings.has_timezone_management
    },
    hasTrainingBuilder() {
      return this.user.organisation_settings.has_training_builder_module
    },
    hasOpenEventApplications() {
      return this.user.organisation_settings.has_open_event_applications_module
    },
    hasNoPlayers() {
      return this.user.organisation_settings.has_no_players
    },
    hasCatapultIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'catapult')
    },
    hasCatapultAutosync() {
      return this.user.organisation_settings.has_catapult_autosync
    },
    hasPolarTeamIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'polar-team')
    },
    hasPolarTeamAutosync() {
      return this.user.organisation_settings.has_polar_team_autosync
    },
    hasGpexeIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'gpexe')
    },
    hasGpexeAutosync() {
      return this.user.organisation_settings.has_gpexe_autosync
    },
    hasKinexonIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'kinexon')
    },
    hasValdIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'vald')
    },
    hasCometIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'comet')
    },
    hasStatsportsApiIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'statsports_api')
    },
    hasWimuIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'wimu')
    },
    activitySourceItems() {
      const orgKeys = (this.user.organisation_settings.integrations || []).map(i => i.key)
      return ACTIVITY_SOURCE_OPTIONS.filter(s => orgKeys.includes(s.key))
    },
    hasDiagnosticsModule() {
      return this.user.organisation_settings.has_diagnostics_module
    },
    repeatItems() {
      if (!this.eventForm.date) return []

      const eventDate = this.$dayjs(this.eventForm.date)

      return [
        { value: 'daily', text: i18n.t('repeat_settings.repeat.every_day') },
        { value: 'weekly', text: i18n.t('repeat_settings.repeat.every_week') + ' ' + eventDate.format('dddd') },
        { value: 'monthly', text: i18n.t('repeat_settings.repeat.every_month') + ' ' + getOrdinal(Math.ceil(eventDate.date() / 7)) + ' ' + eventDate.format('dddd') },
        { value: 'yearly', text: i18n.t('repeat_settings.repeat.every_year') + ' ' + eventDate.format('MMM DD') },
        { value: 'custom', text: i18n.t('repeat_settings.repeat.custom') }
      ]
    },
    repeatUnitItems() {
      return [
        { value: 'day', text: this.$tc('repeat_settings.day', parseInt(this.eventForm.repeat_settings.repeat_interval)) },
        { value: 'week', text: this.$tc('repeat_settings.week', parseInt(this.eventForm.repeat_settings.repeat_interval)) },
        { value: 'month', text: this.$tc('repeat_settings.month', parseInt(this.eventForm.repeat_settings.repeat_interval)) },
        { value: 'year', text: this.$tc('repeat_settings.year', parseInt(this.eventForm.repeat_settings.repeat_interval)) }
      ]
    },
    monthlyModeItems() {
      if (!this.eventForm.date) return []

      const eventDate = this.$dayjs(this.eventForm.date)

      return [
        { value: 'weekday', text: this.$t('repeat_settings.monthly.options.weekday', { ord: getOrdinal(Math.ceil(eventDate.date() / 7)), day: eventDate.format('dddd') }) },
        { value: 'day', text: this.$t('repeat_settings.monthly.options.day', { ord: getOrdinal(Math.ceil(eventDate.date() / 7)) }) }
      ]
    },
    editingRules() {
      const editingRules = this.eventForm.editing_rules
      const activitySource = this.eventForm.activity_source

      return {
        canEditTime: editingRules?.can_edit_time ?? true,
        canEditTeamsAndPlayers: editingRules?.can_edit_teams_and_players ?? true,
        onlyOnePlayer:
          this.eventForm.event_type === this.eventTypeMassage ||
          ['POLAR', 'GARMIN', 'WHOOP'].includes(activitySource) ||
          (!activitySource && (editingRules?.only_one_player ?? false)),
        cannotAddAdditionalPlayers:
          ['POLAR_TEAM', 'GPEXE'].includes(activitySource) ||
          (!activitySource && (editingRules?.cannot_add_additional_players ?? false)),
        canDelete: editingRules?.can_delete ?? true,
        canEditType: editingRules?.can_edit_type ?? true,
        canEditToTraining: editingRules?.can_edit_to_training ?? true,
        canEditToMatch: editingRules?.can_edit_to_match ?? true,
        canEditToOther: editingRules?.can_edit_to_other ?? true,
        canEditToTestSession: editingRules?.can_edit_to_test_session ?? true
      }
    },
    allowedPlayersToSelect() {
      let players
      if (!this.eventForm.id || !this.editingRules.cannotAddAdditionalPlayers) {
        players = this.players.data
      } else {
        const allowedPlayers = this.players.eventPlayers.map(p => p.id)
        players = this.players.data.filter(p => allowedPlayers.indexOf(p.id) > -1)
      }

      if (this.hasDosbFilter && this.dosbAttribute && this.dosbFilterValues.length > 0) {
        const attrId = this.dosbAttribute.id
        players = players.filter(p => (p.attribute_values || []).some(av => av.attribute_id === attrId && this.dosbFilterValues.includes(av.option_key)))
      }

      if (this.isFederation && this.eventForm.player_group_id) {
        players = players.filter(p => p.player_group_id === this.eventForm.player_group_id)
      }

      return players
    },
    hasDosbFilter() {
      return this.user?.organisation_settings?.has_dosb_filter || false
    },
    dosbAttribute() {
      return this.additionalAttributes.data.find(a => a.entity_type === 'player' && a.label === 'DOSB Squad')
    },
    dosbAttributeLabel() {
      if (!this.dosbAttribute) return ''
      return this.dosbAttribute.translations?.[this.$i18n.locale] || this.dosbAttribute.label
    },
    dosbOptions() {
      if (!this.dosbAttribute) return []
      return (this.dosbAttribute.options || []).map(o => ({
        value: o.key,
        text: o.translations?.[this.$i18n.locale] || o.label
      }))
    },
    playersToSelect() {
      return this.allowedPlayersToSelect.slice(0).sort((a, b) => {
        const aSelected = this.eventForm.player_ids.indexOf(a.id) > -1
        const bSelected = this.eventForm.player_ids.indexOf(b.id) > -1

        if (aSelected && !bSelected) return -1
        else if (!aSelected && bSelected) return 1

        return 0
      })
    },
    otherEventTypes() {
      return Object.keys(EventTypes).map(k => {
        return {
          ...EventTypes[k],
          key: k,
          disabled: this.eventForm.id && k === EventType.TEST_SESSION && !this.editingRules.canEditToTestSession
        }
      }).filter(t => t.is_other === true)
    },
    openApplicationStartAt() {
      if (!this.eventForm.date || !this.eventForm.start_at) return undefined

      const daysBefore = parseFloat(this.eventForm.open_application_start_days_before)
      const hoursBefore = parseFloat(this.eventForm.open_application_start_hours_before)

      if (isNaN(daysBefore) && isNaN(hoursBefore)) return undefined

      let date = dayjs(this.eventForm.date + ' ' + dayjs(this.eventForm.start_at, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))

      if (!isNaN(daysBefore)) date = date.subtract(daysBefore, 'day')
      if (!isNaN(hoursBefore)) date = date.subtract(hoursBefore, 'hour')

      return date.toDate()
    },
    openApplicationEndAt() {
      if (!this.eventForm.date || !this.eventForm.start_at) return undefined

      const daysBefore = parseFloat(this.eventForm.open_application_end_days_before)
      const hoursBefore = parseFloat(this.eventForm.open_application_end_hours_before)

      if (isNaN(daysBefore) && isNaN(hoursBefore)) return undefined

      let date = dayjs(this.eventForm.date + ' ' + dayjs(this.eventForm.start_at, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))

      if (!isNaN(daysBefore)) date = date.subtract(daysBefore, 'day')
      if (!isNaN(hoursBefore)) date = date.subtract(hoursBefore, 'hour')

      return date.toDate()
    },
    startAt() {
      if (!this.eventForm.start_at) return undefined

      const startAt = this.$dayjs(this.eventForm.date + ' ' + dayjs(this.eventForm.start_at, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))

      if (!startAt.isValid()) return undefined

      return startAt
    },
    endAt() {
      if (!this.eventForm.end_at) return undefined

      const endAt = this.$dayjs(this.eventForm.date + ' ' + dayjs(this.eventForm.end_at, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))

      if (!endAt.isValid()) return undefined

      return endAt
    },
    endBeforeStart() {
      if (this.startAt && this.endAt && this.endAt.isBefore(this.startAt)) return true

      return false
    },
    massageStats() {
      const stats = {}

      for (const bodyPart of this.eventForm.massage_body_parts) {
        stats[bodyPart] = 3
      }

      return stats
    },
    filteredCometMatches() {
      const competitionIds = this.teamSelectItems
        .filter(team => this.eventForm.team_ids.includes(team.id))
        .flatMap(team => team.comet_competitions)
        .map(comp => comp.external_id)

      return this.externalEvents.comet.data.filter(match => competitionIds.includes(match.competition.id))
    },
    selectedPlayersForProtocol() {
      return this.players.data.filter(p => this.eventForm.player_ids.includes(p.id))
    },
    autoGeneratedTitle() {
      if (!this.isFederation) return ''

      const parts = []

      if (this.eventForm.participant_level) {
        parts.push(this.$t(`event_group.participant_level.${this.eventForm.participant_level}`))
      }

      if (this.eventForm.event_type === this.eventTypeMatch && this.eventForm.is_official_match && this.eventForm.competition_type_id) {
        const competitionType = this.competitionTypes.data.find(c => c.id === this.eventForm.competition_type_id)
        if (competitionType) parts.push(competitionType.name)
      } else if (this.eventForm.event_type === this.eventTypeTraining && this.isFieldHockey && this.eventForm.training_type) {
        parts.push(this.$t(`training_protocol.type.${this.eventForm.training_type}`))
      }

      if (this.eventForm.event_type) {
        parts.push((EventTypes[this.eventForm.event_type] || {}).text || this.$t(`event_type.${this.eventForm.event_type}`))
      }

      return parts.join(' ')
    }
  },
  watch: {
    autoGeneratedTitle: function (newValue) {
      if (this.titleAutoFilled && this.eventForm.title !== newValue) {
        this.eventForm.title = newValue
      }
    },
    'eventForm.title': function (newValue) {
      if (this.titleAutoFilled && newValue && newValue !== this.autoGeneratedTitle) {
        this.titleAutoFilled = false
      }
    },
    'eventForm.event_type': function (newValue) {
      if (newValue !== this.eventTypeReadiness && newValue !== this.eventTypeTraining && newValue !== this.eventTypeMatch) {
        this.trainingProtocol.selected = null
        this.trainingProtocolItems = []
        this.eventForm.player_can_view_training_protocol = true
      }
      if (newValue === this.eventTypeTestSession || newValue === this.eventTypeReadiness) {
        this.fetchAllProtocols()
        this.fetchValdMeasurements()
      } else if (newValue === this.eventTypeMatch) {
        this.fetchAllCompetitionTypes()
        this.fetchAllOpponents()
      } else if (newValue === this.eventTypeMassage) {
        this.fetchMassageTreatmentTypes()
      }
    },
    'eventForm.team_ids': function (oldValue, newValue) {
      this.searchInputTeams = ''

      if (this.editingRules.onlyOnePlayer) return

      const addedTeamIds = difference(oldValue, newValue)
      const removedTeamIds = difference(newValue, oldValue)

      const removePlayerIds = this.playersToSelect.filter(p => (p.team_ids || []).some(tid => removedTeamIds.includes(tid)) && !(p.team_ids || []).some(tid => oldValue.includes(tid))).map(p => p.id)
      const addPlayerIds = this.playersToSelect.filter(p => (p.team_ids || []).some(tid => addedTeamIds.includes(tid))).map(p => p.id)

      this.eventForm.player_ids = union(difference(this.eventForm.player_ids, removePlayerIds), addPlayerIds)

      const removeCoachIds = this.coaches.data.filter(c => intersection(c.teams.filter(t => t.is_coach).map(t => t.id), removedTeamIds).length > 0).map(c => c.id)
      const addCoachIds = this.coaches.data.filter(c => intersection(c.teams.filter(t => t.is_coach).map(t => t.id), addedTeamIds).length > 0).map(c => c.id)

      this.eventForm.coach_ids = union(difference(this.eventForm.coach_ids, removeCoachIds), addCoachIds)
    },
    'eventForm.player_ids': function (playerIds) {
      this.searchInputPlayers = ''
      this.cleanExerciseGroupPlayers(playerIds)
    },
    dosbFilterValues: function () {
      if (!this.dosbAttribute || this.dosbFilterValues.length === 0) return

      const attrId = this.dosbAttribute.id
      const allowedIds = this.allowedPlayersToSelect.map(p => p.id)
      this.eventForm.player_ids = this.eventForm.player_ids.filter(id => {
        const player = this.players.data.find(p => p.id === id)
        if (!player) return false
        const av = (player.attribute_values || []).find(av => av.attribute_id === attrId)
        return av && this.dosbFilterValues.includes(av.option_key) && allowedIds.includes(id)
      })
    },
    'eventForm.player_group_id': function () {
      if (!this.eventForm.player_group_id) return

      const allowedIds = this.allowedPlayersToSelect.map(p => p.id)
      this.eventForm.player_ids = this.eventForm.player_ids.filter(id => allowedIds.includes(id))
    },
    'eventForm.coach_ids': function () {
      this.searchInputCoaches = ''
    },
    'eventForm.start_at': function () {
      this.fetchExternalEventsDebounced()
    },
    'eventForm.end_at': function () {
      this.fetchExternalEventsDebounced()
    },
    'eventForm.date': function () {
      this.fetchExternalEventsDebounced()
      if (this.eventForm.event_type === this.eventTypeTestSession || this.eventForm.event_type === this.eventTypeReadiness) this.fetchValdMeasurements()
    },
    modalVisible(newValue) {
      if (newValue) {
        setTimeout(() => {
          this.$refs.validation.reset()
        }, 500)

        if (this.eventForm.event_type === this.eventTypeTestSession || this.eventForm.event_type === this.eventTypeReadiness) {
          this.fetchAllProtocols()
          this.fetchValdMeasurements()
        } else if (this.eventForm.event_type === this.eventTypeMatch) {
          this.fetchAllCompetitionTypes()
          this.fetchAllOpponents()
        } else if (this.eventForm.event_type === this.eventTypeMassage) {
          this.fetchMassageTreatmentTypes()
        }
        this.fetchAllTrainingProtocols()
        this.fetchAllLocations()
        this.fetchAllCoaches()
        this.fetchAllPlayers()
        this.fetchAdditionalAttributes()
        this.fetchPlayerGroups()
        this.fetchExternalEventsDebounced()
      }
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_EVENT_FORM, ({ startAt, eventType } = {}) => {
      this.titleAutoFilled = false
      this.eventForm = cloneDeep(emptyForm)
      this.eventForm.tz = this.getDefaultTimezone()
      this.repeatMode = 'weekly'
      this.media = []
      this.modalVisible = true

      if (startAt) {
        this.eventForm.date = dayjs(startAt).format('YYYY-MM-DD')
        this.eventForm.start_at = dayjs(startAt).format(getDateTimeFormatForUser('HH:mm'))
        this.eventForm.end_at = dayjs(startAt).add(1, 'hour').format(getDateTimeFormatForUser('HH:mm'))
      }

      this.eventForm.all_day = false

      if (eventType) {
        this.eventForm.event_type = eventType
      } else {
        this.eventTypeSelector = EventType.TRAINING
      }

      this.trainingProtocol.selected = null
      this.trainingProtocolItems = []
      this.hasCatapultEvent = false
      this.hasGpexeEvent = false
      this.hasPolarTeamEvent = false
      this.dosbFilterValues = []
      this.titleAutoFilled = true
      this.$nextTick(() => {
        if (this.isFederation) this.eventForm.title = this.autoGeneratedTitle
      })
    })

    EventBus.$on(this.$events.TRAINING_PROTOCOL_CREATED, (protocol) => {
      this.fetchAllTrainingProtocols()
      this.trainingProtocol.selected = protocol
      this.loadTrainingProtocol(protocol)
      if (!this.trainingProtocol.items.some(tp => tp.id === protocol.id)) {
        this.trainingProtocol.items.push(protocol)
      }
    })

    const items = []

    let startTime = dayjs().startOf('day')
    const day = startTime.day()

    while (startTime.day() === day) {
      items.push(startTime.format(getDateTimeFormatForUser('HH:mm')))
      startTime = startTime.add(15, 'm')
    }

    this.timeItems = items

    this.timezones = timezones

    this.eventTypeMatch = EventType.MATCH
    this.eventTypeTraining = EventType.TRAINING
    this.eventTypeTestSession = EventType.TEST_SESSION
    this.eventTypeTravel = EventType.TRAVEL
    this.eventTypeReadiness = EventType.READINESS
    this.eventTypeRehab = EventType.REHAB
    this.eventTypeIndividualWork = EventType.INDIVIDUAL_WORK
    this.eventTypeMassage = EventType.MASSAGE
    this.eventTypeRest = EventType.REST

    this.travelMeansItems = Object.keys(TravelMeansList).map(k => ({ value: k, text: TravelMeansList[k].text }))

    EventBus.$on(this.$events.EDIT_EVENT, (event) => {
      this.titleAutoFilled = false
      this.eventForm = { ...cloneDeep(emptyForm), ...cloneDeep(event) }
      this.eventForm.tz = this.getDefaultTimezone()
      this.eventForm.date = dayjs(event.start_at).format('YYYY-MM-DD')
      this.eventForm.start_at = dayjs(event.start_at).format(getDateTimeFormatForUser('HH:mm'))
      this.eventForm.end_at = dayjs(event.end_at).format(getDateTimeFormatForUser('HH:mm'))
      this.eventForm.all_day = !!event.all_day
      this.dosbFilterValues = Array.isArray(event.dosb_squad) ? [...event.dosb_squad] : []

      if (event.open_application_start_at) {
        const hoursDiff = dayjs(event.start_at).diff(event.open_application_start_at, 'hour')
        const days = Math.floor(hoursDiff / 24)
        const hours = hoursDiff - (days * 24)

        this.eventForm.open_application_start_days_before = days || ''
        this.eventForm.open_application_start_hours_before = hours || ''
      }

      if (event.open_application_end_at) {
        const hoursDiff = dayjs(event.start_at).diff(event.open_application_end_at, 'hour')
        const days = Math.floor(hoursDiff / 24)
        const hours = hoursDiff - (days * 24)

        this.eventForm.open_application_end_days_before = days || ''
        this.eventForm.open_application_end_hours_before = hours || ''
      }

      if (!this.eventForm.activity_source) {
        if (event.garmin_id) this.eventForm.activity_source = 'GARMIN'
        if (event.whoop_id) this.eventForm.activity_source = 'WHOOP'
        if (event.polar_id) this.eventForm.activity_source = 'POLAR'
      }

      this.players.eventPlayers = event.players
      this.modalVisible = true

      if (this.eventForm.event_type === EventType.TRAINING || this.eventForm.event_type === EventType.MATCH) {
        this.eventTypeSelector = this.eventForm.event_type
      } else {
        this.eventTypeSelector = EventType.OTHER
      }

      if (event.training_protocol) {
        this.trainingProtocol.selected = event.training_protocol

        if (event.training_exercises) {
          this.trainingProtocolItems = cloneDeep(event.training_exercises)
        } else {
          this.loadTrainingProtocol(event.training_protocol)
        }
      } else {
        this.trainingProtocol.selected = undefined
        this.trainingProtocolItems = []
      }

      if (event.media && event.media.length > 0) {
        this.media = event.media.map(m => ({ ...m }))
      } else {
        this.media = []
      }

      if (event.location_id) {
        this.eventForm.location_id = event.location_id
      }

      if (event.competition_type) {
        this.eventForm.competition_type_id = event.competition_type.id
      }

      this.eventForm.training_type = event.training_type ?? null
      this.eventForm.training_method = event.training_method ?? null
      this.eventForm.participant_level = event.participant_level ?? null

      this.titleAutoFilled = this.isFederation && (!this.eventForm.title || this.eventForm.title === this.autoGeneratedTitle)

      if (event.opponent) {
        this.eventForm.opponent = event.opponent
      }

      this.hasCatapultEvent = !!event.catapult_id
      this.hasGpexeEvent = !!event.gpexe_id
      this.hasPolarTeamEvent = !!event.polar_team_id
      this.hasStatsportsApiEvent = !!event.statsports_api_id
      this.hasWimuEvent = !!event.wimu_id

      this.eventForm.comet_id = parseInt(event.comet_id)

      setTimeout(() => {
        this.eventForm.player_ids = event.player_ids
        this.eventForm.coach_ids = event.coach_ids

        this.loadRepeatSchedule()
      }, 0)
    })
  },
  methods: {
    getDateTimeFormatForUser,
    getDefaultTimezone() {
      return this.user?.organisation_settings?.timezone || dayjs.tz.guess()
    },
    async fetchAllProtocols() {
      try {
        this.protocol.itemsLoading = true

        const result = await DiagnosticProtocolApiService.getAll({})

        this.protocol.items = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_protocols.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.protocol.itemsLoading = false
      }
    },
    async fetchAllLocations() {
      try {
        this.location.itemsLoading = true

        const result = await LocationApiService.getAll()

        this.location.items = result.data

        if (this.eventForm.location_id) this.eventForm.location = this.location.items.find(l => l.id === this.eventForm.location_id)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_locations.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.location.itemsLoading = false
      }
    },
    async fetchAllCoaches() {
      try {
        this.coaches.loading = true

        const result = await CoachApiService.getPaginated({})

        this.coaches.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_get.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.coaches.loading = false
      }
    },
    async fetchAllCompetitionTypes() {
      if (this.competitionTypes.loading) return

      try {
        this.competitionTypes.loading = true
        const result = await CompetitionTypeApiService.getAll()
        this.competitionTypes.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.competition_types_get.error'),
          color: 'error'
        })
      } finally {
        this.competitionTypes.loading = false
      }
    },
    async fetchAllOpponents() {
      if (this.opponents.loading) return

      try {
        this.opponents.loading = true
        const result = await MatchOpponentApiService.getAll()
        this.opponents.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.opponents_get.error'),
          color: 'error'
        })
      } finally {
        this.opponents.loading = false
      }
    },
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC', with_attribute_values: this.hasDosbFilter })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    async fetchAdditionalAttributes() {
      if (!this.hasDosbFilter || this.additionalAttributes.loading) return

      try {
        this.additionalAttributes.loading = true
        const result = await AdditionalAttributeApiService.getAll('player')
        this.additionalAttributes.data = result.data
      } catch (e) {
        // best-effort; DOSB filter just won't appear
      } finally {
        this.additionalAttributes.loading = false
      }
    },
    async fetchPlayerGroups() {
      if (!this.isFederation || this.playerGroups.loading) return

      try {
        this.playerGroups.loading = true
        const result = await PlayerGroupApiService.getAll()
        this.playerGroups.data = result.data
      } catch (e) {
        // best-effort; the group filter just won't appear
      } finally {
        this.playerGroups.loading = false
      }
    },
    async fetchAllTrainingProtocols() {
      try {
        this.trainingProtocol.itemsLoading = true

        const result = await TrainingProtocolApiService.getAll({ compact: 1 })

        this.trainingProtocol.items = result.data

        if (this.trainingProtocol.selected && !this.trainingProtocol.items.some(tp => tp.id === this.trainingProtocol.selected.id)) {
          this.trainingProtocol.items.push(cloneDeep(this.trainingProtocol.selected))
        }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_protocols_get.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.trainingProtocol.itemsLoading = false
      }
    },
    async fetchMassageTreatmentTypes() {
      try {
        this.massageTreatmentTypes.loading = true
        this.massageTreatmentTypes.data = await EventApiService.getMassageTreatmentTypes()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.massage_treatment_types_get.error'),
          color: 'error'
        })
      } finally {
        this.massageTreatmentTypes.loading = false
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      if (this.eventForm.event_schedule) {
        this.repeatEditDialog.visible = true
        this.repeatEditDialog.mode = 'all'
        this.repeatEditDialog.deleting = false
        return
      }

      await this.save()
    },
    submitRepeatEditMode() {
      if (this.repeatEditDialog.deleting) {
        this.deleteEvent()
      } else {
        this.repeatEditDialog.visible = false
        this.save()
      }
    },
    onLocationSelected(location) {
      this.eventForm.location = location
    },
    async saveNewLocation(locationData) {
      try {
        const payload = {
          name: locationData.name
        }

        if (locationData.fromGoogle && locationData.geometry && locationData.geometry.location) {
          payload.place_id = locationData.place_id
          payload.latitude = locationData.geometry.location.lat
          payload.longitude = locationData.geometry.location.lng
          payload.address = locationData.formatted_address
        }

        const response = await LocationApiService.create(payload)

        return response.data
      } catch (e) {
        this.eventForm.location = null
      }
    },
    async createOpponent(opponentName) {
      try {
        const existingOpponent = this.opponents.data.find(
          opponent => opponent.name.toLowerCase() === opponentName.toLowerCase()
        )

        if (existingOpponent) {
          this.eventForm.opponent = existingOpponent
          return
        }

        const result = await MatchOpponentApiService.create(opponentName)

        this.opponents.data.push(result.data)

        this.eventForm.opponent = result.data
      } catch (e) {
        this.eventForm.opponent = null
      }
    },
    async save() {
      try {
        this.submitting = true

        if (this.eventForm.location && this.eventForm.location.isNew && this.eventForm.location.name) {
          const newLocation = await this.saveNewLocation(this.eventForm.location)
          this.eventForm.location = newLocation
          this.eventForm.location_id = newLocation.id
        } else if (this.eventForm.location && this.eventForm.location.id) {
          this.eventForm.location_id = this.eventForm.location.id
        }

        if (this.opponents.searchInput && typeof this.opponents.searchInput === 'string') {
          const trimmedOpponent = this.opponents.searchInput.trim()
          if (trimmedOpponent !== '') {
            await this.createOpponent(trimmedOpponent)
            await this.$nextTick()
          }
        }

        let response

        const startAtDayjs = dayjs(this.eventForm.start_at, getDateTimeFormatForUser('HH:mm'))
        const endAtDayjs = dayjs(this.eventForm.end_at, getDateTimeFormatForUser('HH:mm'))

        const startAt = dayjs.tz(this.eventForm.date + ' ' + startAtDayjs.format('HH:mm:ss'), this.eventForm.tz || undefined)
        const endAt = dayjs.tz(this.eventForm.date + ' ' + endAtDayjs.format('HH:mm:ss'), this.eventForm.tz || undefined)

        if (endAt.isBefore(startAt)) endAt.add(1, 'day')

        if (this.eventForm.id) {
          response = await EventApiService.update({
            id: this.eventForm.id,
            title: this.eventForm.title,
            is_player_diagnostic_entry_allowed: this.eventForm.is_player_diagnostic_entry_allowed,
            player_can_view_training_protocol: this.eventForm.player_can_view_training_protocol,
            start_at: startAt.toISOString(),
            end_at: endAt.toISOString(),
            tz: this.eventForm.tz,
            location_id: this.eventForm.location ? this.eventForm.location.id : null,
            diagnostic_protocol_id: this.eventForm.diagnostic_protocol_id,
            sync_vald_data: this.eventForm.sync_vald_data,
            team_ids: this.eventForm.team_ids,
            coach_ids: this.eventForm.coach_ids,
            player_ids: this.eventForm.player_ids,
            event_type: this.eventForm.event_type,
            color: this.eventForm.color_override || null,
            training_type: this.eventForm.event_type === this.eventTypeTraining && this.isFieldHockey ? this.eventForm.training_type : null,
            training_method: this.eventForm.event_type === this.eventTypeTraining && this.isFieldHockey ? this.eventForm.training_method : null,
            participant_level: this.isFederation ? this.eventForm.participant_level : null,
            dosb_squad: this.hasDosbFilter ? this.dosbFilterValues : null,
            player_group_id: this.isFederation ? (this.eventForm.player_group_id || null) : null,
            is_official_match: this.eventForm.is_official_match,
            competition_type_id: this.eventForm.is_official_match ? this.eventForm.competition_type_id : null,
            is_home: this.eventForm.is_home,
            match_opponent_id: this.eventForm.opponent ? this.eventForm.opponent.id : null,
            activity_source: this.eventForm.activity_source,
            catapult_id: this.eventForm.catapult_id,
            resync_catapult_data: this.eventForm.resync_catapult_data,
            gpexe_id: this.eventForm.gpexe_id,
            resync_gpexe_data: this.eventForm.resync_gpexe_data,
            polar_team_id: this.eventForm.polar_team_id,
            resync_polar_team_data: this.eventForm.resync_polar_team_data,
            kinexon_id: this.eventForm.kinexon_id,
            comet_id: this.eventForm.comet_id,
            resync_comet_data: this.eventForm.resync_comet_data,
            statsports_api_id: this.eventForm.statsports_api_id,
            resync_statsports_api_data: this.eventForm.resync_statsports_api_data,
            wimu_id: this.eventForm.wimu_id,
            resync_wimu_data: this.eventForm.resync_wimu_data,
            repeat_settings: {
              repeats: this.eventForm.repeats,
              ...this.eventForm.repeat_settings
            },
            repeat_edit_mode: this.repeatEditDialog.mode,
            training_protocol_id: this.trainingProtocol.selected ? this.trainingProtocol.selected.id : null,
            training_exercises: this.trainingProtocolItems,
            is_open_application: this.eventForm.is_open_application,
            max_participants: this.eventForm.max_participants,
            is_open_application_team_constrained: this.eventForm.is_open_application_team_constrained,
            open_application_start_days_before: this.eventForm.open_application_start_days_before,
            open_application_start_hours_before: this.eventForm.open_application_start_hours_before,
            open_application_end_days_before: this.eventForm.open_application_end_days_before,
            open_application_end_hours_before: this.eventForm.open_application_end_hours_before,
            media_ids: this.media.map(m => m.id),
            travel_means: this.eventForm.travel_means,
            all_day: this.eventForm.all_day,
            massage_treatment_type_id: this.eventForm.massage_treatment_type_id,
            massage_body_parts: this.eventForm.massage_body_parts
          })

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.event_edit.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.EVENT_UPDATED, response.data)
        } else {
          response = await EventApiService.create({
            title: this.eventForm.title,
            description: this.eventForm.description,
            is_description_players_visible: this.eventForm.is_description_players_visible,
            is_note_pinned: this.eventForm.is_note_pinned,
            is_player_diagnostic_entry_allowed: this.eventForm.is_player_diagnostic_entry_allowed,
            player_can_view_training_protocol: this.eventForm.player_can_view_training_protocol,
            start_at: startAt.toISOString(),
            end_at: endAt.toISOString(),
            tz: this.eventForm.tz,
            location_id: this.eventForm.location ? this.eventForm.location.id : null,
            diagnostic_protocol_id: this.eventForm.diagnostic_protocol_id,
            sync_vald_data: this.eventForm.sync_vald_data,
            team_ids: this.eventForm.team_ids,
            coach_ids: this.eventForm.coach_ids,
            player_ids: this.eventForm.player_ids,
            event_type: this.eventForm.event_type,
            color: this.eventForm.color_override || null,
            training_type: this.eventForm.event_type === this.eventTypeTraining && this.isFieldHockey ? this.eventForm.training_type : null,
            training_method: this.eventForm.event_type === this.eventTypeTraining && this.isFieldHockey ? this.eventForm.training_method : null,
            participant_level: this.isFederation ? this.eventForm.participant_level : null,
            dosb_squad: this.hasDosbFilter ? this.dosbFilterValues : null,
            player_group_id: this.isFederation ? (this.eventForm.player_group_id || null) : null,
            is_official_match: this.eventForm.is_official_match,
            competition_type_id: this.eventForm.is_official_match ? this.eventForm.competition_type_id : null,
            is_home: this.eventForm.is_home,
            match_opponent_id: this.eventForm.opponent ? this.eventForm.opponent.id : null,
            activity_source: this.eventForm.activity_source,
            catapult_id: this.eventForm.catapult_id,
            gpexe_id: this.eventForm.gpexe_id,
            polar_team_id: this.eventForm.polar_team_id,
            kinexon_id: this.eventForm.kinexon_id,
            comet_id: this.eventForm.comet_id,
            statsports_api_id: this.eventForm.statsports_api_id,
            wimu_id: this.eventForm.wimu_id,
            repeat_settings: {
              repeats: this.eventForm.repeats,
              ...this.eventForm.repeat_settings
            },
            training_protocol_id: this.trainingProtocol.selected ? this.trainingProtocol.selected.id : null,
            training_exercises: this.trainingProtocolItems,
            is_open_application: this.eventForm.is_open_application,
            max_participants: this.eventForm.max_participants,
            is_open_application_team_constrained: this.eventForm.is_open_application_team_constrained,
            open_application_start_days_before: this.eventForm.open_application_start_days_before,
            open_application_start_hours_before: this.eventForm.open_application_start_hours_before,
            open_application_end_days_before: this.eventForm.open_application_end_days_before,
            open_application_end_hours_before: this.eventForm.open_application_end_hours_before,
            media_ids: this.media.map(m => m.id),
            travel_means: this.eventForm.travel_means,
            all_day: this.eventForm.all_day,
            massage_treatment_type_id: this.eventForm.massage_treatment_type_id,
            massage_body_parts: this.eventForm.massage_body_parts
          })

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.event_save.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.EVENT_CREATED, response.data)
        }

        this.modalVisible = false

        this.eventForm = cloneDeep(emptyForm)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.event_save.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.submitting = false
      }
    },
    initDelete() {
      if (this.eventForm.event_schedule) {
        this.repeatEditDialog.deleting = true
        this.repeatEditDialog.visible = true
        this.repeatEditDialog.mode = 'this'
        return
      }

      this.deleteConfirmationDialogVisible = true
    },
    async deleteEvent() {
      try {
        this.deleting = true

        await EventApiService.delete(this.eventForm.id, {
          repeat_edit_mode: this.repeatEditDialog.mode
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.event_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.EVENT_DELETED, this.eventForm)

        this.repeatEditDialog.visible = false
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.event_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    processRepeatItemChange() {
      this.eventForm.repeat_settings.end_at = dayjs(this.eventForm.date).add(1, 'year').format('YYYY-MM-DD')

      if (this.repeatMode === 'daily') {
        this.eventForm.repeat_settings.repeat_unit = 'day'
        this.eventForm.repeat_settings.repeat_interval = 1
        return
      }

      if (this.repeatMode === 'weekly') {
        this.eventForm.repeat_settings.repeat_unit = 'week'
        this.eventForm.repeat_settings.repeat_interval = 1

        const day = this.eventForm.date ? dayjs(this.eventForm.date).day() : 1
        this.eventForm.repeat_settings.constraints.weekdays = [day === 0 ? 7 : day]

        return
      }

      if (this.repeatMode === 'monthly') {
        this.eventForm.repeat_settings.repeat_unit = 'month'
        this.eventForm.repeat_settings.repeat_interval = 1
        this.eventForm.repeat_settings.constraints.monthly_mode = 'weekday'
        return
      }

      if (this.repeatMode === 'yearly') {
        this.eventForm.repeat_settings.repeat_unit = 'year'
        this.eventForm.repeat_settings.repeat_interval = 1
      }
    },
    loadRepeatSchedule() {
      if (!this.eventForm.event_schedule) return

      this.eventForm.repeats = true

      const repeatSettings = {
        repeat_unit: this.eventForm.event_schedule.repeat_unit,
        repeat_interval: this.eventForm.event_schedule.repeat_interval,
        constraints: this.eventForm.event_schedule.constraints,
        end_after_occurrences: this.eventForm.event_schedule.end_after_occurrences || 12,
        end_at: this.eventForm.event_schedule.end_at || dayjs(this.eventForm.date).add(1, 'year').format('YYYY-MM-DD'),
        end_mode: this.eventForm.event_schedule.end_at ? 'end_at' : (this.eventForm.event_schedule.end_after_occurrences ? 'end_after_occurrences' : 'never')
      }

      this.eventForm.repeat_settings = repeatSettings

      if (repeatSettings.repeat_unit === 'day' && repeatSettings.repeat_interval === 1) this.repeatMode = 'daily'
      else if (repeatSettings.repeat_unit === 'week' && repeatSettings.repeat_interval === 1 && repeatSettings.constraints.weekdays.length === 1) this.repeatMode = 'weekly'
      else if (repeatSettings.repeat_unit === 'month' && repeatSettings.repeat_interval === 1 && repeatSettings.constraints.monthly_mode === 'weekday') this.repeatMode = 'monthly'
      else if (repeatSettings.repeat_unit === 'year' && repeatSettings.repeat_interval === 1) this.repeatMode = 'yearly'
      else this.repeatMode = 'custom'
    },
    async loadTrainingProtocol(protocol) {
      if (!protocol) return

      this.trainingProtocol.itemsLoading = true

      try {
        const result = await TrainingProtocolApiService.getDetails(protocol.id)

        this.trainingProtocolItems = cloneDeep(result.data?.exercises || [])
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_protocols_get.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.trainingProtocol.itemsLoading = false
      }
    },
    fetchExternalEventsDebounced: debounce(function () {
      this.fetchCatapultEvents()
      this.fetchGpexeEvents()
      this.fetchPolarTeamEvents()
      this.fetchKinexonEvents()
      this.fetchCometMatches()
      this.fetchStatsportsApiEvents()
      this.fetchWimuEvents()
    }, 1000),
    async fetchCatapultEvents() {
      if (!this.hasCatapultIntegration) return

      try {
        this.externalEvents.catapult.loading = true

        if (this.hasCatapultAutosync) {
          const response = await ExternalEventApiService.getEvents({
            from: this.eventForm.date ? dayjs(this.eventForm.date).subtract(1, 'day').format('YYYY-MM-DD') : undefined,
            to: this.eventForm.date ? dayjs(this.eventForm.date).add(1, 'day').format('YYYY-MM-DD') : undefined,
            integration_type: IntegrationType.CATAPULT
          })

          this.externalEvents.catapult.data = response.data
        } else {
          const response = await ExternalEventApiService.getCatapultActivities({
            start_date: this.eventForm.date ? dayjs(this.eventForm.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
          })

          this.externalEvents.catapult.data = response.data
        }
      } catch (e) {

      } finally {
        this.externalEvents.catapult.loading = false
      }
    },
    async fetchGpexeEvents() {
      if (!this.hasGpexeIntegration) return

      try {
        this.externalEvents.gpexe.loading = true

        if (this.hasGpexeAutosync) {
          const response = await ExternalEventApiService.getEvents({
            from: this.eventForm.date ? dayjs(this.eventForm.date).subtract(1, 'day').format('YYYY-MM-DD') : undefined,
            to: this.eventForm.date ? dayjs(this.eventForm.date).add(1, 'day').format('YYYY-MM-DD') : undefined,
            integration_type: IntegrationType.GPEXE
          })

          this.externalEvents.gpexe.data = response.data
        } else {
          const response = await ExternalEventApiService.getGpexeActivities({
            start_date: this.eventForm.date ? dayjs(this.eventForm.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
          })

          this.externalEvents.gpexe.data = response.data.map(event => {
            return {
              ...event,
              external_id: event.external_id.toString()
            }
          })
        }
      } catch (e) {

      } finally {
        this.externalEvents.gpexe.loading = false
      }
    },
    async fetchPolarTeamEvents() {
      if (!this.hasPolarTeamIntegration) return

      try {
        this.externalEvents.polarTeam.loading = true

        if (this.hasPolarTeamAutosync) {
          const response = await ExternalEventApiService.getEvents({
            from: this.eventForm.date ? dayjs(this.eventForm.date).subtract(1, 'day').format('YYYY-MM-DD') : undefined,
            to: this.eventForm.date ? dayjs(this.eventForm.date).add(1, 'day').format('YYYY-MM-DD') : undefined,
            integration_type: IntegrationType.POLAR_TEAM
          })

          this.externalEvents.polarTeam.data = response.data
        } else {
          const response = await ExternalEventApiService.getPolarTeamActivities({
            start_date: this.eventForm.date ? dayjs(this.eventForm.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
          })

          this.externalEvents.polarTeam.data = response.data
        }
      } catch (e) {

      } finally {
        this.externalEvents.polarTeam.loading = false
      }
    },
    async fetchKinexonEvents() {
      if (!this.hasKinexonIntegration) return

      try {
        this.externalEvents.kinexon.loading = true

        const response = await ExternalEventApiService.getEvents({
          from: this.eventForm.date ? dayjs(this.eventForm.date).subtract(1, 'day').format('YYYY-MM-DD') : undefined,
          to: this.eventForm.date ? dayjs(this.eventForm.date).add(1, 'day').format('YYYY-MM-DD') : undefined,
          integration_type: IntegrationType.KINEXON
        })

        this.externalEvents.kinexon.data = response.data
      } catch (e) {

      } finally {
        this.externalEvents.kinexon.loading = false
      }
    },
    async fetchCometMatches() {
      if (!this.hasCometIntegration) return

      try {
        this.externalEvents.comet.loading = true

        const response = await CometApiService.getMatches({
          date: this.eventForm.date ? dayjs(this.eventForm.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
        })

        this.externalEvents.comet.data = response.data || []
      } catch (e) {

      } finally {
        this.externalEvents.comet.loading = false
      }
    },
    async fetchStatsportsApiEvents() {
      if (!this.hasStatsportsApiIntegration) return

      try {
        this.externalEvents.statsportsApi.loading = true

        const response = await ExternalEventApiService.getStatsportsActivities({
          start_date: this.eventForm.date ? dayjs(this.eventForm.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
        })

        this.externalEvents.statsportsApi.data = response.data
      } catch (e) {

      } finally {
        this.externalEvents.statsportsApi.loading = false
      }
    },
    async fetchWimuEvents() {
      if (!this.hasWimuIntegration) return

      try {
        this.externalEvents.wimu.loading = true

        const response = await ExternalEventApiService.getWimuActivities({
          start_date: this.eventForm.date ? dayjs(this.eventForm.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
        })

        this.externalEvents.wimu.data = response.data
      } catch (e) {

      } finally {
        this.externalEvents.wimu.loading = false
      }
    },
    async fetchValdMeasurements() {
      if (!this.hasValdIntegration) return

      try {
        this.valdMeasurements.loading = true

        const result = await ValdMeasurementApiService.getAll({
          date: dayjs(this.eventForm.date).format('YYYY-MM-DD')
        })

        this.valdMeasurements.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_vald_measurements.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.valdMeasurements.loading = false
      }
    },
    updateTrainingProtocolItems(items) {
      this.trainingProtocolItems = cloneDeep(items)
    },
    cleanExerciseGroupPlayers(validPlayerIds) {
      const cleanGroups = (groups) => {
        if (!groups || !Array.isArray(groups)) return
        groups.forEach(group => {
          if (group.player_ids) group.player_ids = group.player_ids.filter(id => validPlayerIds.includes(id))
          if (group.inside_joker_ids) group.inside_joker_ids = group.inside_joker_ids.filter(id => validPlayerIds.includes(id))
          if (group.outside_joker_ids) group.outside_joker_ids = group.outside_joker_ids.filter(id => validPlayerIds.includes(id))
        })
      }

      const cleanExercises = (exercises) => {
        if (!exercises) return
        exercises.forEach(exercise => {
          if (exercise.params?.SETS) {
            exercise.params.SETS.forEach(set => cleanGroups(set.groups))
          }
          if (exercise.exercises) cleanExercises(exercise.exercises)
        })
      }

      cleanExercises(this.trainingProtocolItems)
    },
    async handleFileChange() {
      await this.$nextTick()

      const files = [...this.$refs.uploadFile.files]

      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dkg', 'csv']

      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()

        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }

      this.$nextTick(() => {
        this.$vuetify.goTo('.media-grid-item', { container: '.v-dialog.v-dialog--active .v-card__text' })
      })

      this.processUpload()
    },
    async processUpload() {
      let formData

      for (const file of this.media.filter(m => !!m.upload)) {
        formData = new FormData()
        formData.append('file', file.file, file.name)

        _axios.post('media/upload', formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false

          Object.assign(file, response.data.media)
        }).catch(error => {
          if (error.response && error.response.status === 422) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              color: 'error',
              text: this.$t('alert.file_type_not_allowed.error')
            })

            file.error = this.$t('alert.file_type_not_allowed.error')

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('event_details.media_upload.error')
          })

          file.error = this.$t('event_details.media_upload.error')
        })
      }
    },
    async destroyMedia(event) {
      if (!this.deleteMedia.media) return

      const id = this.deleteMedia.media.id

      try {
        this.deleteMedia.deleting = true

        await MediaApiService.delete(id)

        const index = this.media.findIndex(m => m.id === id)

        if (index > -1) this.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteMedia.dialog = false

        EventBus.$emit(this.$events.TRAINING_EXERCISE_UPDATED, this.trainingExerciseFormData)
      } catch (e) {

      } finally {
        this.deleteMedia.media = undefined
        this.deleteMedia.deleting = false
      }
    },
    dragover(e) {
      this.fileUpload.isDragging = true
    },
    dragleave(e) {
      this.fileUpload.isDragging = false
    },
    dragenter(e) {
      this.fileUpload.isDragging = true
    },
    drop(e) {
      this.$refs.uploadFile.files = e.dataTransfer.files
      this.handleFileChange()
      this.fileUpload.isDragging = false
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    startMediaDelete(media) {
      this.deleteMedia.media = media
      this.deleteMedia.dialog = true
    },
    onAllDayToggle() {
      if (this.eventForm.all_day) {
        this.eventForm.start_at = '00:00'
        this.eventForm.end_at = '23:59'
      } else if (this.eventForm.start_at === '00:00' && this.eventForm.end_at === '23:59') {
        this.eventForm.start_at = undefined
        this.eventForm.end_at = undefined
      }
    },
    massageMuscleToggle(muscle) {
      const index = this.eventForm.massage_body_parts.indexOf(muscle)

      if (index > -1) {
        this.eventForm.massage_body_parts.splice(index, 1)
      } else {
        this.eventForm.massage_body_parts.push(muscle)
      }
    }
  }
}
</script>
